(() => {
  'use strict';

  const SELECTOR = '[data-wv-calm]';

  function createWatchvaCalm(root) {
    if (!root || root.__wvCalmInitialized) return;
    root.__wvCalmInitialized = true;

    let destroyed = false;
    const audioEnabled = root.dataset.audioEnabled !== 'false';
    const externalMusic = root.dataset.externalMusic !== 'false';
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    const get = (id) => root.querySelector(`[data-wv-id="${id}"]`);

    const timeoutIds = new Set();
    const intervalIds = new Set();
    const audioTimeoutIds = new Set();
    const audioIntervalIds = new Set();

    const later = (fn, ms = 0, ...args) => {
      const id = window.setTimeout(() => {
        timeoutIds.delete(id);
        if (!destroyed) fn(...args);
      }, ms);
      timeoutIds.add(id);
      return id;
    };
    const repeat = (fn, ms = 0, ...args) => {
      const id = window.setInterval(() => {
        if (!destroyed) fn(...args);
      }, ms);
      intervalIds.add(id);
      return id;
    };
    const clearLater = (id) => {
      if (id == null) return;
      window.clearTimeout(id);
      timeoutIds.delete(id);
    };
    const clearRepeat = (id) => {
      if (id == null) return;
      window.clearInterval(id);
      intervalIds.delete(id);
    };

    const audioLater = (fn, ms = 0, ...args) => {
      const id = window.setTimeout(() => {
        audioTimeoutIds.delete(id);
        if (!destroyed) fn(...args);
      }, ms);
      audioTimeoutIds.add(id);
      return id;
    };
    const audioRepeat = (fn, ms = 0, ...args) => {
      const id = window.setInterval(() => {
        if (!destroyed) fn(...args);
      }, ms);
      audioIntervalIds.add(id);
      return id;
    };
    const clearAudioLater = (id) => {
      if (id == null) return;
      window.clearTimeout(id);
      audioTimeoutIds.delete(id);
    };
    const clearAudioRepeat = (id) => {
      if (id == null) return;
      window.clearInterval(id);
      audioIntervalIds.delete(id);
    };
    const clearAudioTimers = () => {
      audioTimeoutIds.forEach((id) => window.clearTimeout(id));
      audioIntervalIds.forEach((id) => window.clearInterval(id));
      audioTimeoutIds.clear();
      audioIntervalIds.clear();
    };

/* ═══════════════════════
   THEMES
═══════════════════════ */
const T = {
	breathe: {
		bg: "#0e0904",
		accent: [232, 96, 38],
		accentHex: "#e86026",
		vigA: "rgba(8,4,1,.54)",
		vigB: "rgba(4,2,0,.92)",
		textMain: "#ede7d8",
		textDim: "rgba(245,238,220,.26)",
		cueCol: "rgba(232,96,38,.75)",
		display: "Breathe",
		heroIdle: ["make room", "for one breath"],
		heroIdleAccent: -1,
		heroAct: ["stay with", "this one"],
		heroActAccent: -1,
		tagIdle: "For whatever is still following you",
		tagAct: "inhale / hold / exhale / hold",
		cueStart: "begin where you are",
		pattern: "Box breath",
		detail: "4 · 4 · 4 · 4",
		phases: [
			{ n: "inhale", d: 4 },
			{ n: "hold", d: 4 },
			{ n: "exhale", d: 4 },
			{ n: "hold", d: 4 }
		],
		barCol: "rgba(232,96,38,.55)",
		barLbl: "rgba(245,232,212,.45)",
		slPat: "rgba(245,232,212,.55)",
		slDet: "rgba(232,96,38,.7)",
		dotCol: "#e86026",
		ringCol: "rgba(232,96,38,.6)",
		// nav
		navBg: "rgba(14,6,2,.82)",
		navBorder: "rgba(232,96,38,.28)",
		pillActive: "#fff0e0",
		pillDim: "rgba(245,225,200,.82)",
		pillActiveBg: "#e86026",
		pillDimBg: "transparent",
		thumbBg: "rgba(232,96,38,.45)",
		flashCol: "rgba(232,96,38,.07)"
	},
	meditate: {
		bg: "#060510",
		accent: [148, 105, 255],
		accentHex: "#9469ff",
		vigA: "rgba(3,2,12,.58)",
		vigB: "rgba(1,1,7,.94)",
		textMain: "#dfd7f5",
		textDim: "rgba(220,210,255,.24)",
		cueCol: "rgba(148,105,255,.75)",
		display: "Still",
		heroIdle: ["turn down", "the input"],
		heroIdleAccent: -1,
		heroAct: ["nothing else", "needs you"],
		heroActAccent: -1,
		tagIdle: "A slower pattern for a crowded head",
		tagAct: "inhale / hold / longer exhale",
		cueStart: "let one thing become quieter",
		pattern: "4–7–8 breath",
		detail: "4 · 7 · 8",
		phases: [
			{ n: "inhale", d: 4 },
			{ n: "hold", d: 7 },
			{ n: "exhale", d: 8 }
		],
		barCol: "rgba(148,105,255,.55)",
		barLbl: "rgba(220,210,255,.45)",
		slPat: "rgba(220,210,255,.55)",
		slDet: "rgba(148,105,255,.7)",
		dotCol: "#9469ff",
		ringCol: "rgba(148,105,255,.6)",
		// nav
		navBg: "rgba(6,3,18,.85)",
		navBorder: "rgba(148,105,255,.28)",
		pillActive: "#efe6ff",
		pillDim: "rgba(210,195,255,.82)",
		pillActiveBg: "#9469ff",
		pillDimBg: "transparent",
		thumbBg: "rgba(148,105,255,.45)",
		flashCol: "rgba(148,105,255,.06)"
	},
	anxiety: {
		bg: "#020904",
		accent: [60, 185, 98],
		accentHex: "#3cb962",
		vigA: "rgba(1,6,2,.58)",
		vigB: "rgba(0,4,1,.94)",
		textMain: "#cce8d4",
		textDim: "rgba(190,228,202,.24)",
		cueCol: "rgba(60,185,98,.75)",
		display: "Ground",
		heroIdle: ["come back", "to what is here"],
		heroIdleAccent: -1,
		heroAct: ["one thing", "is enough"],
		heroActAccent: -1,
		tagIdle: "When everything feels louder than it needs to",
		tagAct: "inhale / hold / longer exhale",
		cueStart: "name one thing you know",
		pattern: "Steady breath",
		detail: "4 · 4 · 6",
		phases: [
			{ n: "inhale", d: 4 },
			{ n: "hold", d: 4 },
			{ n: "exhale", d: 6 }
		],
		barCol: "rgba(60,185,98,.52)",
		barLbl: "rgba(190,228,202,.45)",
		slPat: "rgba(190,228,202,.55)",
		slDet: "rgba(60,185,98,.7)",
		dotCol: "#3cb962",
		ringCol: "rgba(60,185,98,.6)",
		// nav
		navBg: "rgba(2,12,4,.85)",
		navBorder: "rgba(60,185,98,.25)",
		pillActive: "#e0ffea",
		pillDim: "rgba(175,228,190,.82)",
		pillActiveBg: "#3cb962",
		pillDimBg: "transparent",
		thumbBg: "rgba(60,185,98,.45)",
		flashCol: "rgba(60,185,98,.05)"
	},
	nature: {
		bg: "#030d0e",
		accent: [105, 195, 215],
		accentHex: "#69c3d7",
		vigA: "rgba(1,7,8,.5)",
		vigB: "rgba(0,5,6,.88)",
		textMain: "#d2ecf0",
		textDim: "rgba(148,220,235,.28)",
		cueCol: "rgba(105,195,215,.75)",
		display: "Rain",
		heroIdle: ["listen to", "something simple"],
		heroIdleAccent: -1,
		heroAct: ["let the rain", "hold the room"],
		heroActAccent: -1,
		tagIdle: "Rain / lake / wind",
		tagAct: "let the exhale follow the rain",
		cueStart: "listen before you count",
		pattern: "Rain breath",
		detail: "5 in · 6 out",
		phases: [
			{ n: "inhale", d: 5 },
			{ n: "exhale", d: 6 }
		],
		barCol: "rgba(105,195,215,.5)",
		barLbl: "rgba(148,220,235,.45)",
		slPat: "rgba(148,220,235,.55)",
		slDet: "rgba(105,195,215,.7)",
		dotCol: "#69c3d7",
		ringCol: "rgba(105,195,215,.6)",
		// nav
		navBg: "rgba(2,14,18,.85)",
		navBorder: "rgba(105,195,215,.25)",
		pillActive: "#e6f7fb",
		pillDim: "rgba(155,215,230,.82)",
		pillActiveBg: "#69c3d7",
		pillDimBg: "transparent",
		thumbBg: "rgba(105,195,215,.45)",
		flashCol: "rgba(105,195,215,.05)"
	},
	classical: {
		bg: "#f4ece0",
		accent: [152, 22, 42],
		accentHex: "#98162a",
		vigA: "rgba(185,162,135,.3)",
		vigB: "rgba(155,128,98,.52)",
		textMain: "#221416",
		textDim: "rgba(55,22,28,.38)",
		cueCol: "rgba(132,16,36,.78)",
		display: "Classical",
		heroIdle: ["follow one", "quiet phrase"],
		heroIdleAccent: -1,
		heroAct: ["stay with", "the phrase"],
		heroActAccent: -1,
		tagIdle: "Music / breath / one slower pace",
		tagAct: "inhale with the phrase / exhale longer",
		cueStart: "let the music set the pace",
		pattern: "Phrase breath",
		detail: "4 in · 6 out",
		phases: [
			{ n: "inhale", d: 4 },
			{ n: "exhale", d: 6 }
		],
		barCol: "rgba(132,16,36,.45)",
		barLbl: "rgba(55,22,28,.5)",
		slPat: "rgba(55,22,28,.55)",
		slDet: "rgba(132,16,36,.7)",
		dotCol: "#98162a",
		ringCol: "rgba(152,22,42,.5)",
		// nav — light parchment feel
		navBg: "rgba(244,235,218,.92)",
		navBorder: "rgba(152,22,42,.28)",
		pillActive: "#ffe6e8",
		pillDim: "rgba(130,60,40,.82)",
		pillActiveBg: "#98162a",
		pillDimBg: "transparent",
		thumbBg: "rgba(152,22,42,.45)",
		flashCol: "rgba(152,22,42,.05)"
	},
	jazz: {
		bg: "#0d0804",
		accent: [238, 162, 24],
		accentHex: "#eea218",
		vigA: "rgba(6,3,0,.44)",
		vigB: "rgba(4,2,0,.84)",
		textMain: "#ffedbc",
		textDim: "rgba(255,210,115,.3)",
		cueCol: "rgba(238,162,24,.82)",
		display: "Jazz",
		heroIdle: ["let the rhythm", "carry the count"],
		heroIdleAccent: -1,
		heroAct: ["stay with", "the rhythm"],
		heroActAccent: -1,
		tagIdle: "Rhythm / breath / less to manage",
		tagAct: "let the rhythm carry the breath",
		cueStart: "listen for the next count",
		pattern: "Rhythm breath",
		detail: "3 in · 4 out",
		phases: [
			{ n: "inhale", d: 3 },
			{ n: "exhale", d: 4 }
		],
		barCol: "rgba(238,162,24,.55)",
		barLbl: "rgba(255,210,115,.45)",
		slPat: "rgba(255,210,115,.55)",
		slDet: "rgba(238,162,24,.7)",
		dotCol: "#eea218",
		ringCol: "rgba(238,162,24,.65)",
		// nav — warm dark amber
		navBg: "rgba(22,10,0,.88)",
		navBorder: "rgba(238,162,24,.30)",
		pillActive: "#fff7e6",
		pillDim: "rgba(255,210,140,.82)",
		pillActiveBg: "#eea218",
		pillDimBg: "transparent",
		thumbBg: "rgba(238,162,24,.45)",
		flashCol: "rgba(238,162,24,.07)"
	}
};

const AFFIRMATIONS = [
	"Not everything that feels urgent needs action tonight.",
	"Name one thing you know. Let the rest stay unanswered for a minute.",
	"A difficult feeling does not have to become a conclusion.",
	"You can let one non-urgent thing wait.",
	"One useful next step is enough for now.",
	"You do not have to rehearse the whole conversation here.",
	"Your body is allowed to take longer than your thoughts want it to.",
	"Set down the part that cannot be solved in this minute.",
	"The next ten minutes can be smaller than the whole day.",
	"A pause does not need to earn its place by being productive.",
	"You can care about something without carrying all of it at once.",
	"Let the exhale be longer than the thought loop.",
	"Return to what is directly in front of you.",
	"You can decide later. Right now, notice what is here.",
	"One breath does not solve everything. It only gives this moment less to hold."
];

const BASE_C = "https://archive.org/download/classical_music_202209/";
const CLASSICAL_TRACKS = [
	{
		url: BASE_C + "Beethoven%20-%20Moonlight%20Sonata.mp3",
		name: "Beethoven — Moonlight Sonata"
	},
	{
		url: BASE_C + "Chopin%20-%20Nocturne%20No%202.mp3",
		name: "Chopin — Nocturne No. 2"
	},
	{
		url: BASE_C + "Beethoven%20-%20Fur%20Elise.mp3",
		name: "Beethoven — Für Elise"
	},
	{
		url: BASE_C + "Chopin%20-%20Minute%20Waltz.mp3",
		name: "Chopin — Minute Waltz"
	}
];
const JAZZ_TRACKS = [
	{
		url:
			"https://archive.org/download/78_west-end-blues_louis-armstrong-and-his-hot-five-louis-armstrong_gbia0001057a/West%20End%20Blues%20-%20Louis%20Armstrong.mp3",
		name: "Louis Armstrong — West End Blues"
	},
	{
		url:
			"https://archive.org/download/78_saint-louis-blues_bessie-smith-louis-armstrong-w-c-handy_gbia0001052b/Saint%20Louis%20Blues%20-%20Bessie%20Smith.mp3",
		name: "Bessie Smith — Saint Louis Blues"
	},
	{
		url:
			"https://archive.org/download/78_stardust_hoagy-carmichael_gbia0001352a/Stardust%20-%20Hoagy%20Carmichael.mp3",
		name: "Hoagy Carmichael — Stardust"
	},
	{
		url:
			"https://archive.org/download/78_tiger-rag_original-dixieland-jass-band_gbia0000141b/Tiger%20Rag%20-%20Original%20Dixieland%20Jass%20Band.mp3",
		name: "Original Dixieland Jazz Band — Tiger Rag"
	},
	{
		url:
			"https://archive.org/download/78_indiana_original-dixieland-jazz-band_gbia0000143a/Indiana%20-%20Original%20Dixieland%20Jazz%20Band.mp3",
		name: "Original Dixieland Jazz Band — Indiana"
	}
];
let musicAudio = null;

/* ═══ CANVAS ══ */
const bgC = get("c-bg"),
	fxC = get("c-fx"),
	tpC = get("c-top");
const bgX = bgC.getContext("2d"),
	fxX = fxC.getContext("2d"),
	tpX = tpC.getContext("2d");
let W = 1, H = 1, DPR = 1;
function resize() {
	const rect = root.getBoundingClientRect();
	W = Math.max(1, Math.round(rect.width));
	H = Math.max(1, Math.round(rect.height));
	DPR = Math.min(window.devicePixelRatio || 1, 2);
	[bgC, fxC, tpC].forEach((canvas) => {
		canvas.width = Math.max(1, Math.round(W * DPR));
		canvas.height = Math.max(1, Math.round(H * DPR));
	});
	[bgX, fxX, tpX].forEach((ctx) => ctx.setTransform(DPR, 0, 0, DPR, 0, 0));
}
const resizeObserver = new ResizeObserver(resize);
resizeObserver.observe(root);
resize();

/* ═══ STATE ══ */
let mode = root.dataset.defaultMode || "breathe",
	isActive = false,
	phase = 0,
	timer = 0,
	bs = 0,
	breathCount = 0,
	exhaleVal = 0;
let textAnim = 0,
	charData = [],
	cueChars = [];
let natureCycleT = 0;

const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches || false;
function sceneProfile() {
	const mobile = W < 760;
	const compact = W < 480;
	return {
		mobile,
		compact,
		motion: reduceMotion ? 0.12 : mobile ? 0.5 : 1,
		pointer: coarsePointer || mobile ? 0.28 : 1,
		detail: compact ? 0.56 : mobile ? 0.7 : 1
	};
}
function modeTextAnim(m) {
	return ({ breathe: 0, meditate: 1, anxiety: 2, nature: 3, classical: 4, jazz: 5 })[m] || 0;
}

/* ═══ MOUSE + DRAG STATE ══ */
function localPoint(clientX, clientY) {
	const rect = root.getBoundingClientRect();
	return {
		x: Math.max(0, Math.min(W, clientX - rect.left)),
		y: Math.max(0, Math.min(H, clientY - rect.top))
	};
}
let mx = 0.5,
	my = 0.5,
	rmx = 0.5,
	rmy = 0.5;
// Velocity for drag effect
let pmx = 0.5,
	pmy = 0.5,
	mvx = 0,
	mvy = 0;
// Click/drag impulse
let clickImpulse = 0,
	isDragging = false,
	dragEnergy = 0;
// Click ripple positions for wave distortion
let clickRipples = [];

root.addEventListener("mousedown", (e) => {
	const p = localPoint(e.clientX, e.clientY);
	isDragging = true;
	clickImpulse = 1.0;
	clickRipples.push({
		x: p.x / W,
		y: p.y / H,
		t: 0,
		strength: 1.0
	});
	if (clickRipples.length > 6) clickRipples.shift();
});
root.addEventListener("mouseup", () => {
	isDragging = false;
});
root.addEventListener("mousemove", (e) => {
	const p = localPoint(e.clientX, e.clientY);
	pmx = mx;
	pmy = my;
	mx = p.x / W;
	my = p.y / H;
	mvx = (mx - pmx) * 60;
	mvy = (my - pmy) * 60;
	if (isDragging) {
		dragEnergy = Math.min(
			dragEnergy + Math.sqrt(mvx * mvx + mvy * mvy) * 0.04,
			1.5
		);
		if (Math.random() < 0.08) {
			clickRipples.push({
				x: mx,
				y: my,
				t: 0,
				strength: 0.4 + dragEnergy * 0.3
			});
			if (clickRipples.length > 8) clickRipples.shift();
		}
	}
	const dotEl = get("dot");
	dotEl.style.left = p.x + "px";
	dotEl.style.top = p.y + "px";
});
root.addEventListener(
	"touchstart",
	(e) => {
		isDragging = true;
		const t2 = e.touches[0];
		const p = localPoint(t2.clientX, t2.clientY);
		clickImpulse = 1.0;
		clickRipples.push({
			x: p.x / W,
			y: p.y / H,
			t: 0,
			strength: 1.0
		});
		if (clickRipples.length > 6) clickRipples.shift();
	},
	{ passive: true }
);
root.addEventListener("touchend", () => {
	isDragging = false;
});
root.addEventListener(
	"touchmove",
	(e) => {
		const t2 = e.touches[0];
		const p = localPoint(t2.clientX, t2.clientY);
		pmx = mx;
		pmy = my;
		mx = p.x / W;
		my = p.y / H;
		mvx = (mx - pmx) * 20;
		mvy = (my - pmy) * 20;
		if (isDragging) {
			dragEnergy = Math.min(
				dragEnergy + Math.sqrt(mvx * mvx + mvy * mvy) * 0.015,
				0.72
			);
		}
	},
	{ passive: true }
);

/* lazy smoothing for cursor ring */
const dotEl = get("dot"),
	ringEl = get("ring");
let csx = 0,
	csy = 0,
	crx = 0,
	cry = 0;
root.addEventListener("mousemove", (e) => {
	const p = localPoint(e.clientX, e.clientY);
	csx = p.x;
	csy = p.y;
});
let cursorRaf = 0;
(function ac() {
	if (destroyed) return;
	crx += (csx - crx) * 0.11;
	cry += (csy - cry) * 0.11;
	rmx += (mx - rmx) * 0.04;
	rmy += (my - rmy) * 0.04;
	// decay
	clickImpulse *= 0.94;
	dragEnergy *= 0.97;
	// age ripples
	for (let i = clickRipples.length - 1; i >= 0; i--) {
		clickRipples[i].t += 0.016;
		if (clickRipples[i].t > 2.5) clickRipples.splice(i, 1);
	}
	mvx *= 0.88;
	mvy *= 0.88;
	ringEl.style.left = crx + "px";
	ringEl.style.top = cry + "px";
	cursorRaf = requestAnimationFrame(ac);
})();

/* ═══ RIPPLES ══ */
function spawnRipple(x, y) {
	const el = document.createElement("div");
	el.className = "ripple";
	const th = T[mode],
		[ar, ag, ab] = th.accent,
		sz = 68 + bs * 52;
	el.style.cssText = `left:${x}px;top:${y}px;width:${sz}px;height:${sz}px;border:1.5px solid rgba(${ar},${ag},${ab},.55)`;
	root.appendChild(el);
	later(() => el.remove(), 880);
}
root.addEventListener("click", (e) => {
	if (e.target.closest("[data-wv-id=\"mode-switcher\"]") || e.target.closest("[data-wv-id=\"mode-trigger\"]") || e.target.closest("[data-wv-id=\"mode-layer\"]") || e.target.closest("[data-wv-id=\"breath-btn\"]"))
		return;
	const p = localPoint(e.clientX, e.clientY);
	spawnRipple(p.x, p.y);
});
root.addEventListener(
	"touchstart",
	(e) => {
		[...e.touches].forEach((t3) => {
			if (!e.target.closest("[data-wv-id=\"mode-switcher\"]") && !e.target.closest("[data-wv-id=\"mode-trigger\"]") && !e.target.closest("[data-wv-id=\"mode-layer\"]") && !e.target.closest("[data-wv-id=\"breath-btn\"]"))
				{
					const p = localPoint(t3.clientX, t3.clientY);
					spawnRipple(p.x, p.y);
				}
		});
	},
	{ passive: true }
);

/* ═══ TEXT BUILD ══ */
function buildHeroText(lines, tag) {
	const h1 = get("h1-text");
	h1.innerHTML = "";
	charData = [];
	const th = T[mode];
	if (mode === "classical") {
		h1.style.fontFamily = "'Playfair Display',serif";
		h1.style.fontWeight = "300";
		h1.style.fontStyle = "italic";
		h1.style.letterSpacing = "-0.01em";
	} else if (mode === "jazz") {
		h1.style.fontFamily = "'Syne',sans-serif";
		h1.style.fontWeight = "800";
		h1.style.fontStyle = "normal";
		h1.style.letterSpacing = "0.04em";
	} else {
		h1.style.fontFamily = "'Cormorant Garamond',serif";
		h1.style.fontWeight = "300";
		h1.style.fontStyle = "italic";
		h1.style.letterSpacing = "-0.02em";
	}

	lines.forEach((lineText, li) => {
		const lineEl = document.createElement("div");
		lineEl.style.cssText = "display:block;text-align:center;";
		const words = lineText.split(" ");
		words.forEach((w, wi) => {
			if (wi > 0) {
				const sp = document.createElement("span");
				sp.className = "word-space";
				lineEl.appendChild(sp);
			}
			const wrap = document.createElement("span");
			wrap.className = "word-wrap";
			lineEl.appendChild(wrap);
			[...w].forEach((ch, ci) => {
				const s = document.createElement("span");
				s.className = "char";
				s.textContent = ch;
				s.style.color = th.textMain;
				wrap.appendChild(s);
				charData.push({
					el: s,
					line: li,
					word: wi,
					idx: ci,
					phase: Math.random() * Math.PI * 2,
					freq: 0.00036 + Math.random() * 0.00025,
					amp: 3 + Math.random() * 5.5
				});
			});
		});
		h1.appendChild(lineEl);
	});

	const allChars = h1.querySelectorAll(".word-wrap:last-child .char");
	allChars.forEach((c) => (c.style.color = th.accentHex));

	const tgEl = get("tagline-text");
	tgEl.textContent = tag;
	tgEl.style.color = th.textDim;
}

function buildCueText(txt) {
	const el = get("cue");
	el.innerHTML = "";
	cueChars.length = 0;
	[...txt].forEach((ch, i) => {
		if (ch === " ") {
			el.appendChild(document.createTextNode(" "));
			return;
		}
		const s = document.createElement("span");
		s.className = "cue-char";
		s.textContent = ch;
		el.appendChild(s);
		cueChars.push({
			el: s,
			i,
			phase: i * 0.17,
			freq: 0.00046 + i * 0.000016,
			amp: 2 + Math.random() * 3
		});
	});
	el.style.color = T[mode].cueCol;
}

/* ═══ TEXT ANIMATION — art-directed per mode, restrained on mobile ══ */
function animateText(ts) {
	const p = sceneProfile();
	const pointerX = (rmx - 0.5) * 3.2 * p.pointer;
	const pointerY = (rmy - 0.5) * 2.4 * p.pointer;
	const breath = isActive ? bs : 0.22 + Math.sin(ts * 0.00045) * 0.06;

	charData.forEach((c) => {
		const wave = Math.sin(ts * c.freq * 0.72 + c.phase + c.idx * 0.16);
		const wave2 = Math.cos(ts * c.freq * 0.4 + c.line * 1.7 + c.idx * 0.11);
		let tx = 0, ty = 0, rot = 0, sc = 1, op = 1;

		if (mode === "breathe") {
			ty = wave * c.amp * 0.28 * (0.55 + breath * 0.45) + pointerY * 0.18;
			tx = wave2 * c.amp * 0.08 + pointerX * 0.12;
		} else if (mode === "meditate") {
			ty = wave * c.amp * 0.16 + pointerY * 0.1;
			sc = 1 + (0.008 + breath * 0.018) * Math.sin(ts * 0.0005 + c.idx * 0.13);
		} else if (mode === "anxiety") {
			ty = -Math.abs(wave) * c.amp * 0.1 * (0.5 + breath * 0.25);
			tx = pointerX * 0.06;
		} else if (mode === "nature") {
			tx = wave2 * c.amp * 0.11 + pointerX * 0.1;
			ty = wave * c.amp * 0.14 + pointerY * 0.08;
		} else if (mode === "classical") {
			ty = -Math.max(0, wave) * c.amp * 0.18 * (0.7 + breath * 0.3);
			sc = 1 + breath * 0.012 * Math.sin(c.idx * 0.35 + ts * 0.0007);
		} else {
			tx = wave2 * c.amp * 0.14 + pointerX * 0.12;
			ty = wave * c.amp * 0.12;
			rot = wave * 0.55;
		}

		tx *= p.motion;
		ty *= p.motion;
		rot *= p.motion;
		sc = 1 + (sc - 1) * p.motion;
		c.el.style.transform = `translate(${tx.toFixed(2)}px,${ty.toFixed(2)}px) rotate(${rot.toFixed(2)}deg) scale(${sc.toFixed(3)})`;
		c.el.style.opacity = op;
	});

	cueChars.forEach((c) => {
		const y = Math.sin(ts * c.freq * 0.6 + c.phase) * (isActive ? 1.6 + bs * 1.5 : 0.8) * p.motion;
		c.el.style.transform = `translateY(${y.toFixed(2)}px)`;
	});
	get("tagline-text").style.transform = `translateY(${(Math.sin(ts * 0.00032 + 5) * 0.8 * p.motion).toFixed(2)}px)`;
}

/* ═══ ORGANIC SHAPE HELPERS — low-frequency composition, local pointer influence ══ */
function ribbonPath(ctx, t, yFrac, amp, speed, phase, pointerPull) {
	const p = sceneProfile();
	const segments = p.mobile ? 5 : 7;
	const pts = [];
	for (let i = 0; i <= segments; i++) {
		const nx = i / segments;
		const local = Math.exp(-Math.pow((nx - rmx) / 0.24, 2));
		const y =
			H * yFrac +
			Math.sin(t * speed + nx * Math.PI * 2 + phase) * amp +
			Math.sin(t * speed * 0.47 + nx * Math.PI * 1.15 + phase * 1.7) * amp * 0.28 +
			(rmy - 0.5) * amp * (pointerPull || 0) * local * p.pointer +
			dragEnergy * amp * 0.09 * local * p.pointer;
		pts.push({ x: nx * W, y });
	}
	ctx.beginPath();
	ctx.moveTo(pts[0].x, pts[0].y);
	for (let i = 1; i < pts.length - 1; i++) {
		const mx2 = (pts[i].x + pts[i + 1].x) / 2;
		const my2 = (pts[i].y + pts[i + 1].y) / 2;
		ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx2, my2);
	}
	const last = pts[pts.length - 1];
	ctx.lineTo(last.x, last.y);
}

function fillRibbon(ctx, t, yFrac, amp, speed, phase, color, pointerPull) {
	ribbonPath(ctx, t, yFrac, amp, speed, phase, pointerPull);
	ctx.lineTo(W, H);
	ctx.lineTo(0, H);
	ctx.closePath();
	ctx.fillStyle = color;
	ctx.fill();
}

function strokeRibbon(ctx, t, yFrac, amp, speed, phase, color, width, pointerPull) {
	ribbonPath(ctx, t, yFrac, amp, speed, phase, pointerPull);
	ctx.strokeStyle = color;
	ctx.lineWidth = width;
	ctx.stroke();
}

function drawSoftEllipse(ctx, cx, cy, rx, ry, color, width, rotation) {
	ctx.save();
	ctx.translate(cx, cy);
	ctx.rotate(rotation || 0);
	ctx.beginPath();
	ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
	ctx.strokeStyle = color;
	ctx.lineWidth = width;
	ctx.stroke();
	ctx.restore();
}

function drawMouseWave(t, [ar, ag, ab], alpha) {
	const p = sceneProfile();
	if (p.mobile || reduceMotion) return;
	const amp = H * (0.012 + bs * 0.018) * (1 + dragEnergy * 0.3);
	strokeRibbon(
		tpX,
		t,
		0.56 + (rmy - 0.5) * 0.05,
		amp,
		0.00012,
		1.4,
		`rgba(${ar},${ag},${ab},${alpha * (0.45 + bs * 0.45)})`,
		0.8,
		0.18
	);
}

/* ══════════════════════════════════════
   1. BREATHE
══════════════════════════════════════ */
function drawBreathe(t, bs, ev) {
	const p = sceneProfile();
	bgX.fillStyle = "#0e0904";
	bgX.fillRect(0, 0, W, H);

	const cx = W * (0.5 + (rmx - 0.5) * 0.035 * p.pointer);
	const cy = H * (p.mobile ? 0.62 : 0.57);
	const aura = bgX.createRadialGradient(cx, cy, 0, cx, cy, W * (p.mobile ? 0.58 : 0.46));
	aura.addColorStop(0, `rgba(232,96,38,${0.12 + bs * 0.18})`);
	aura.addColorStop(0.35, `rgba(150,48,12,${0.06 + bs * 0.08})`);
	aura.addColorStop(1, "transparent");
	bgX.fillStyle = aura;
	bgX.fillRect(0, 0, W, H);

	const ampScale = p.mobile ? 0.58 : 1;
	const motion = 0.55 + bs * 0.32;
	[
		[0.83, 104, 0.000055, 0.2, `rgba(62,21,7,${0.92})`],
		[0.73, 82, 0.000068, 1.3, `rgba(91,29,8,${0.68 + bs * 0.05})`],
		[0.64, 60, 0.000083, 2.1, `rgba(126,39,10,${0.46 + bs * 0.07})`],
		[0.56, 39, 0.000101, 3.0, `rgba(177,56,15,${0.24 + bs * 0.08})`]
	].forEach(([y, a, sp, ph, col]) => {
		fillRibbon(bgX, t, y, a * ampScale * motion, sp, ph, col, 0.22);
	});

	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);
	const contourY = p.mobile ? 0.61 : 0.55;
	strokeRibbon(fxX, t, contourY, (p.mobile ? 18 : 28) * (0.65 + bs * 0.55), 0.00012, 0.8, `rgba(246,222,199,${0.12 + bs * 0.16})`, 0.85, 0.16);
	strokeRibbon(fxX, t, contourY + 0.075, (p.mobile ? 13 : 20) * (0.6 + bs * 0.45), 0.000095, 2.1, `rgba(232,96,38,${0.14 + bs * 0.22})`, 1.1, 0.14);

	clickRipples.forEach((r) => {
		const age = r.t;
		const radius = W * (0.035 + age * 0.08) * r.strength;
		const a = Math.max(0, 0.14 * (1 - age / 2.5));
		fxX.beginPath();
		fxX.arc(r.x * W, r.y * H, radius, 0, Math.PI * 2);
		fxX.strokeStyle = `rgba(232,96,38,${a})`;
		fxX.lineWidth = 0.8;
		fxX.stroke();
	});
}

/* ══════════════════════════════════════
   2. MEDITATE
══════════════════════════════════════ */
function drawMeditate(t, bs, ev) {
	const p = sceneProfile();
	const bg = bgX.createLinearGradient(0, 0, 0, H);
	bg.addColorStop(0, "#03020c");
	bg.addColorStop(0.55, "#060516");
	bg.addColorStop(1, "#02020d");
	bgX.fillStyle = bg;
	bgX.fillRect(0, 0, W, H);

	const cx = W * (0.5 + (rmx - 0.5) * 0.025 * p.pointer);
	const cy = H * (p.mobile ? 0.5 : 0.47);
	const glow = bgX.createRadialGradient(cx, cy, 0, cx, cy, W * 0.55);
	glow.addColorStop(0, `rgba(148,105,255,${0.095 + bs * 0.17})`);
	glow.addColorStop(0.42, `rgba(71,43,165,${0.045 + bs * 0.075})`);
	glow.addColorStop(1, "transparent");
	bgX.fillStyle = glow;
	bgX.fillRect(0, 0, W, H);

	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);
	const base = Math.min(W, H);
	const haloScale = 0.94 + bs * 0.08;
	[
		[0.34, 0.135, 0.12, 1.1, 0.58],
		[0.43, 0.175, 0.075, 2.2, 0.42],
		[0.52, 0.215, 0.045, 3.4, 0.32]
	].forEach(([rx, ry, alpha, phase, lw], i) => {
		const driftX = Math.sin(t * (0.00007 + i * 0.00001) + phase) * base * 0.012 * p.motion;
		const driftY = Math.cos(t * (0.00006 + i * 0.000012) + phase) * base * 0.008 * p.motion;
		drawSoftEllipse(
			fxX,
			cx + driftX,
			cy + driftY,
			base * rx * haloScale,
			base * ry * (0.95 + bs * 0.06),
			`rgba(190,170,255,${alpha + bs * 0.055})`,
			lw,
			Math.sin(t * 0.000035 + phase) * 0.08 * p.motion
		);
	});

	const count = p.mobile ? 42 : 84;
	for (let i = 0; i < count; i++) {
		const sx = ((((Math.sin(i * 41.7) * 43758.5) % 1) + 1) % 1) * W;
		const sy = ((((Math.sin(i * 37.1 + 1) * 43758.5) % 1) + 1) % 1) * H * 0.76;
		const tw = 0.25 + 0.75 * Math.abs(Math.sin(t * 0.00072 + i * 1.21));
		const sr = 0.35 + ((((Math.sin(i * 29.4 + 2) * 43758.5) % 1) + 1) % 1) * 0.7;
		fxX.beginPath();
		fxX.arc(sx, sy, sr, 0, Math.PI * 2);
		fxX.fillStyle = `rgba(224,216,255,${(0.045 + bs * 0.07) * tw})`;
		fxX.fill();
	}
}

/* ══════════════════════════════════════
   3. ANXIETY
══════════════════════════════════════ */
function drawAnxiety(t, bs, ev) {
	const p = sceneProfile();
	const bg = bgX.createLinearGradient(0, 0, 0, H);
	bg.addColorStop(0, "#010702");
	bg.addColorStop(0.55, "#020d05");
	bg.addColorStop(1, "#010803");
	bgX.fillStyle = bg;
	bgX.fillRect(0, 0, W, H);

	const cx = W * (0.5 + (rmx - 0.5) * 0.025 * p.pointer);
	const glow = bgX.createRadialGradient(cx, H * 0.48, 0, cx, H * 0.48, W * 0.52);
	glow.addColorStop(0, `rgba(60,185,98,${0.07 + bs * 0.12})`);
	glow.addColorStop(0.45, `rgba(25,78,38,${0.035 + bs * 0.05})`);
	glow.addColorStop(1, "transparent");
	bgX.fillStyle = glow;
	bgX.fillRect(0, 0, W, H);

	const ampScale = p.mobile ? 0.55 : 1;
	fillRibbon(bgX, t, 0.86, 82 * ampScale, 0.00004, 0.5, "rgba(0,14,5,.82)", 0.08);
	fillRibbon(bgX, t, 0.76, 58 * ampScale, 0.000052, 1.7, "rgba(5,29,12,.64)", 0.09);
	fillRibbon(bgX, t, 0.67, 36 * ampScale, 0.000064, 2.6, `rgba(15,55,25,${0.34 + bs * 0.04})`, 0.1);

	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);
	strokeRibbon(fxX, t, p.mobile ? 0.68 : 0.64, (p.mobile ? 13 : 18) * (0.65 + bs * 0.3), 0.000075, 1.2, `rgba(140,220,160,${0.08 + bs * 0.1})`, 0.75, 0.08);

	const count = p.mobile ? 14 : 24;
	for (let i = 0; i < count; i++) {
		const fx = ((((Math.sin(i * 23.7) * 43758) % 1) + 1) % 1) * W;
		const fy0 = ((((Math.sin(i * 17.1) * 43758) % 1) + 1) % 1) * H * 0.55 + H * 0.12;
		const fy = fy0 + ev * (6 + (i % 3) * 2);
		const tw = 0.25 + 0.75 * Math.abs(Math.sin(t * 0.0007 + i * 1.65));
		const a = (0.035 + bs * 0.055) * tw;
		const gg = fxX.createRadialGradient(fx, fy, 0, fx, fy, p.mobile ? 6 : 9);
		gg.addColorStop(0, `rgba(182,244,174,${a})`);
		gg.addColorStop(1, "transparent");
		fxX.fillStyle = gg;
		fxX.beginPath();
		fxX.arc(fx, fy, p.mobile ? 6 : 9, 0, Math.PI * 2);
		fxX.fill();
	}
}

/* ══════════════════════════════════════
   4. NATURE — tranquil lake, full-screen rain, wind waves
══════════════════════════════════════ */
function RNG(seed) {
	let s = seed;
	return () => {
		s = (s * 16807) % 2147483647;
		return (s - 1) / 2147483646;
	};
}

// Lake ripple system — mouse creates ripples on lake
const LAKE_RIPPLES = [];
let lastLakeRipple = 0;

// Raindrops — full screen, dense
const RAIN_COUNT = 420;
const GOLDEN_SCATTER = 0.6180339887498949;

function fract(n) {
	return n - Math.floor(n);
}

// Stable pseudo-random value for a droplet + fall cycle.
// Unlike Math.random(), this does not shimmer from frame to frame.
function rainHash(dropIndex, cycle, salt = 0) {
	const n = Math.sin(
		(dropIndex + 1) * 12.9898 +
		(cycle + 1) * 78.233 +
		(salt + 1) * 37.719
	) * 43758.5453123;
	return fract(n);
}

const RAINDROPS2 = Array.from({ length: RAIN_COUNT }, (_, i) => {
	const r = RNG(i * 71 + 3);
	return {
		// Keep the original first RNG draw so every later reference property
		// (y/speed/length/alpha/thickness) retains its exact sequence.
		// Live X placement is generated by scatteredRainX() on each respawn.
		x: r() * 1.2 - 0.1,
		y: r(),
		speed: 6 + r() * 10,
		len: 9 + r() * 22,
		angle: -0.06 + r() * 0.04,
		alpha: 0.12 + r() * 0.22,
		thickness: 0.4 + r() * 0.8,
		scatterJitter: r(),
		scatterPhase: r() * Math.PI * 2
	};
});

// Deliberately unbounded. The integer portion is the fall/respawn cycle;
// the fractional portion is the droplet's vertical position.
let rainY = RAINDROPS2.map((d) => d.y);

function scatteredRainX(drop, i, cycle, t) {
	// Golden-ratio spacing prevents accidental columns and giant empty holes.
	const stratified = fract(
		(i + 0.5) * GOLDEN_SCATTER +
		cycle * 0.3819660112501051
	);

	// Enough deterministic jitter to keep the spacing organic instead of grid-like.
	const jitter =
		(rainHash(i, cycle, 1) - 0.5) * 0.22 +
		(drop.scatterJitter - 0.5) * 0.055;

	// Very slow sheet drift. This moves the rain field as a whole without
	// making individual drops wiggle while they are falling.
	const sheetDrift =
		Math.sin(t * 0.000055 + drop.scatterPhase) * 0.012;

	return -0.08 + fract(stratified + jitter + sheetDrift + 1) * 1.16;
}

// Wind bands
const WIND_BANDS = Array.from({ length: 12 }, (_, i) => {
	const r = RNG(i * 53 + 7);
	return {
		y: r(),
		speed: 0.00018 + r() * 0.00028,
		amp: 0.018 + r() * 0.032,
		freq: 0.8 + r() * 1.8,
		phase: r() * Math.PI * 2
	};
});

// Lake reflection ripple seeds
const LAKE_RING_SEEDS = Array.from({ length: 22 }, (_, i) => {
	const r = RNG(i * 29 + 3);
	return {
		x: 0.15 + r() * 0.7,
		spawnInterval: 2000 + r() * 5000,
		lastSpawn: r() * 5000
	};
});

function drawNature(t, bs, ev, dtMs = 16.667) {
	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);

	// ── Sky gradient ──
	const skyGrad = bgX.createLinearGradient(0, 0, 0, H);
	// Moody overcast lake sky — blue-grey tones
	const overcast = isActive ? Math.min(bs * 0.6, 0.5) : 0.15;
	skyGrad.addColorStop(
		0,
		`rgb(${Math.round(18 + overcast * 8)},${Math.round(
			28 + overcast * 10
		)},${Math.round(42 + overcast * 15)})`
	);
	skyGrad.addColorStop(
		0.35,
		`rgb(${Math.round(28 + overcast * 10)},${Math.round(
			48 + overcast * 14
		)},${Math.round(72 + overcast * 20)})`
	);
	skyGrad.addColorStop(
		0.62,
		`rgb(${Math.round(48 + overcast * 12)},${Math.round(
			72 + overcast * 16
		)},${Math.round(92 + overcast * 22)})`
	);
	skyGrad.addColorStop(
		1,
		`rgb(${Math.round(22 + overcast * 8)},${Math.round(
			38 + overcast * 10
		)},${Math.round(55 + overcast * 16)})`
	);
	bgX.fillStyle = skyGrad;
	bgX.fillRect(0, 0, W, H);

	// Mouse glow in sky
	const skyGlow = bgX.createRadialGradient(
		W * rmx,
		H * rmy * 0.6,
		0,
		W * rmx,
		H * rmy * 0.6,
		W * (0.3 + dragEnergy * 0.1)
	);
	skyGlow.addColorStop(
		0,
		`rgba(105,195,215,${0.04 + bs * 0.08 + dragEnergy * 0.04})`
	);
	skyGlow.addColorStop(1, "transparent");
	bgX.fillStyle = skyGlow;
	bgX.fillRect(0, 0, W, H);

	// ── Mist layers ──
	for (let m = 0; m < 4; m++) {
		const my2 = 0.38 + m * 0.06;
		const mistX =
			(rmx - 0.5) * W * 0.03 * (m + 1) +
			Math.sin(t * 0.00008 * (m + 1)) * W * 0.04;
		const mist = bgX.createRadialGradient(
			W * 0.5 + mistX,
			H * my2,
			0,
			W * 0.5 + mistX,
			H * my2,
			W * (0.55 + m * 0.1)
		);
		mist.addColorStop(0, `rgba(155,195,215,${0.04 + m * 0.012 + bs * 0.02})`);
		mist.addColorStop(0.5, `rgba(105,155,185,${0.02 + m * 0.008})`);
		mist.addColorStop(1, "transparent");
		bgX.fillStyle = mist;
		bgX.fillRect(0, 0, W, H);
	}

	// ── Distant hills / shoreline ──
	const hillY = H * 0.48;
	// Far hill
	bgX.beginPath();
	bgX.moveTo(0, H);
	for (let x = 0; x <= W; x += 4) {
		const nx = x / W;
		const hy =
			hillY +
			Math.sin(nx * Math.PI * 1.4 + 0.5) * H * 0.06 +
			Math.sin(nx * Math.PI * 3.2) * H * 0.022;
		x === 0 ? bgX.moveTo(0, hy) : bgX.lineTo(x, hy);
	}
	bgX.lineTo(W, H);
	bgX.closePath();
	bgX.fillStyle = "rgba(15,30,25,0.72)";
	bgX.fill();
	// Near hill with pine silhouettes
	const nearHillY = H * 0.52;
	bgX.beginPath();
	for (let x = 0; x <= W; x += 4) {
		const nx = x / W;
		const hy =
			nearHillY +
			Math.sin(nx * Math.PI * 2.1 + 1.2) * H * 0.05 +
			Math.sin(nx * Math.PI * 5.8) * H * 0.012;
		x === 0 ? bgX.moveTo(0, hy) : bgX.lineTo(x, hy);
	}
	bgX.lineTo(W, H);
	bgX.closePath();
	bgX.fillStyle = "rgba(8,18,12,0.88)";
	bgX.fill();
	// Pine tree silhouettes on near ridge
	for (let i = 0; i < 32; i++) {
		const r2 = RNG(i * 41 + 9);
		const tx2 = r2() * W;
		const nx = tx2 / W;
		const baseY =
			nearHillY +
			Math.sin(nx * Math.PI * 2.1 + 1.2) * H * 0.05 +
			Math.sin(nx * Math.PI * 5.8) * H * 0.012;
		const th2 = H * (0.038 + r2() * 0.045);
		const tw2 = th2 * 0.38;
		// wind sway
		const sway =
			(rmx - 0.5) * tw2 * 0.4 +
			Math.sin(t * 0.00022 + i * 0.7) * tw2 * 0.18 * (1 + bs * 0.4);
		bgX.beginPath();
		bgX.moveTo(tx2 + sway, baseY);
		bgX.lineTo(tx2 - tw2 / 2 + sway * 0.7, baseY - th2 * 0.45);
		bgX.lineTo(tx2 + sway * 1.1, baseY - th2);
		bgX.lineTo(tx2 + tw2 / 2 - sway * 0.7, baseY - th2 * 0.45);
		bgX.closePath();
		bgX.fillStyle = "rgba(4,12,7,0.92)";
		bgX.fill();
		bgX.beginPath();
		bgX.moveTo(tx2 + sway, baseY - th2 * 0.4);
		bgX.lineTo(tx2 - (tw2 * 0.42) / 2 + sway * 0.8, baseY - th2 * 0.75);
		bgX.lineTo(tx2 + sway * 1.05, baseY - th2 * 1.12);
		bgX.lineTo(tx2 + (tw2 * 0.42) / 2 - sway * 0.8, baseY - th2 * 0.75);
		bgX.closePath();
		bgX.fill();
	}

	// ── Lake surface ──
	const lakeTop = H * 0.535;
	const lakeGrad = bgX.createLinearGradient(0, lakeTop, 0, H);
	lakeGrad.addColorStop(0, "rgba(18,38,55,0.95)");
	lakeGrad.addColorStop(0.3, "rgba(12,28,44,0.98)");
	lakeGrad.addColorStop(0.7, "rgba(8,20,32,1)");
	lakeGrad.addColorStop(1, "rgba(4,14,22,1)");
	bgX.fillStyle = lakeGrad;
	bgX.fillRect(0, lakeTop, W, H - lakeTop);

	// Sky reflection on lake
	const reflGrad = bgX.createLinearGradient(0, lakeTop, 0, H);
	reflGrad.addColorStop(0, `rgba(55,95,135,${0.18 + bs * 0.08})`);
	reflGrad.addColorStop(0.4, `rgba(28,58,85,${0.1 + bs * 0.05})`);
	reflGrad.addColorStop(1, "rgba(8,22,35,0)");
	bgX.fillStyle = reflGrad;
	bgX.fillRect(0, lakeTop, W, H - lakeTop);

	// Mouse glow on lake
	const lakeMouseGlow = bgX.createRadialGradient(
		W * rmx,
		Math.max(lakeTop + 20, H * (0.6 + rmy * 0.3)),
		0,
		W * rmx,
		Math.max(lakeTop + 20, H * (0.6 + rmy * 0.3)),
		W * (0.2 + dragEnergy * 0.08)
	);
	lakeMouseGlow.addColorStop(
		0,
		`rgba(105,195,215,${0.05 + dragEnergy * 0.06 + clickImpulse * 0.04})`
	);
	lakeMouseGlow.addColorStop(1, "transparent");
	bgX.fillStyle = lakeMouseGlow;
	bgX.fillRect(0, lakeTop, W, H - lakeTop);

	// Click ripples on lake
	clickRipples.forEach((r) => {
		const ry2 = r.y * H;
		if (ry2 < lakeTop) return;
		const rg = bgX.createRadialGradient(
			r.x * W,
			ry2,
			0,
			r.x * W,
			ry2,
			W * 0.14 * r.strength
		);
		rg.addColorStop(
			0,
			`rgba(105,195,215,${r.strength * 0.07 * Math.exp(-r.t * 1.0)})`
		);
		rg.addColorStop(1, "transparent");
		bgX.fillStyle = rg;
		bgX.fillRect(0, lakeTop, W, H - lakeTop);
	});

	// ── Lake wave ripples (concentric, from rain) ──
	// Auto-spawn lake ripples from seeds
	LAKE_RING_SEEDS.forEach((seed) => {
		seed.lastSpawn += 16;
		if (seed.lastSpawn > seed.spawnInterval) {
			seed.lastSpawn = 0;
			LAKE_RIPPLES.push({
				x: seed.x * W,
				y: lakeTop + Math.random() * (H - lakeTop) * 0.8,
				r: 0,
				maxR: W * (0.04 + Math.random() * 0.08),
				alpha: 0.28 + bs * 0.15,
				speed: 0.8 + Math.random() * 0.6
			});
		}
	});
	// Spawn ripples from click on lake area
	clickRipples.forEach((r) => {
		const ry2 = r.y * H;
		if (ry2 >= lakeTop && Math.random() < 0.4) {
			LAKE_RIPPLES.push({
				x: r.x * W,
				y: ry2,
				r: 0,
				maxR: W * (0.06 + r.strength * 0.06),
				alpha: 0.4 * r.strength,
				speed: 1.2 + r.strength
			});
		}
	});
	// Draw and age lake ripples
	for (let i = LAKE_RIPPLES.length - 1; i >= 0; i--) {
		const lr = LAKE_RIPPLES[i];
		lr.r += lr.speed;
		const progress = lr.r / lr.maxR;
		if (progress >= 1) {
			LAKE_RIPPLES.splice(i, 1);
			continue;
		}
		const a = lr.alpha * (1 - progress) * (1 - progress);
		// elliptical ripple (perspective)
		fxX.beginPath();
		fxX.ellipse(lr.x, lr.y, lr.r, lr.r * 0.32, 0, 0, Math.PI * 2);
		fxX.strokeStyle = `rgba(155,210,228,${a})`;
		fxX.lineWidth = 0.8 + progress * 0.4;
		fxX.stroke();
		// second inner ring
		if (lr.r > 8) {
			fxX.beginPath();
			fxX.ellipse(lr.x, lr.y, lr.r * 0.65, lr.r * 0.65 * 0.32, 0, 0, Math.PI * 2);
			fxX.strokeStyle = `rgba(155,210,228,${a * 0.45})`;
			fxX.lineWidth = 0.5;
			fxX.stroke();
		}
	}

	// ── Lake horizontal wave lines ──
	const windPush = (rmx - 0.5) * 0.4;
	for (let w2 = 0; w2 < 18; w2++) {
		const wyFrac = lakeTop / H + ((w2 + 1) / 19) * (1 - lakeTop / H);
		const wyAbs = wyFrac * H;
		const wAmp =
			H *
			(0.003 + w2 * 0.0008) *
			(0.5 + bs * 0.7) *
			(1 + Math.abs(rmx - 0.5) * 0.8 + dragEnergy * 0.5);
		const wSpeed = 0.00012 + w2 * 0.000018;
		fxX.beginPath();
		for (let x = 0; x <= W; x += 3) {
			const nx = x / W;
			let y =
				wyAbs +
				Math.sin(
					nx * Math.PI * 4 * (1 + w2 * 0.15) +
						t * wSpeed * (w2 + 1) * 4 +
						windPush * nx * 6
				) *
					wAmp +
				Math.sin(nx * Math.PI * 7 * (1 + w2 * 0.08) + t * wSpeed * (w2 + 0.5) * 6) *
					wAmp *
					0.38 +
				(rmx - 0.5) * wAmp * 0.6 * Math.sin(nx * Math.PI * 2.5);
			// click ripple distortion on lake
			for (const r of clickRipples) {
				const rx2 = r.x,
					ry2 = r.y;
				if (ry2 * H < lakeTop) continue;
				const dx = nx - rx2,
					age = r.t,
					wf = age * 0.45,
					sp2 = 0.08 + age * 0.12;
				y +=
					r.strength *
					wAmp *
					0.8 *
					Math.exp(-Math.pow(Math.abs(dx) - wf, 2) / (sp2 * sp2)) *
					Math.exp(-age * 0.8) *
					Math.sin((Math.abs(dx) - wf) * 25);
			}
			x === 0 ? fxX.moveTo(0, y) : fxX.lineTo(x, y);
		}
		const wAlpha = 0.03 + w2 * 0.008 + bs * 0.04;
		fxX.strokeStyle = `rgba(135,195,215,${wAlpha * (1 + dragEnergy * 0.4)})`;
		fxX.lineWidth = 0.5 + w2 * 0.04;
		fxX.stroke();
	}

	// ── Wind bands — horizontal streaks across sky ──
	WIND_BANDS.forEach((band, i) => {
		const windY = band.y * H * 0.52; // only in sky portion
		const windOffset = (t * band.speed + (rmx - 0.5) * 0.02) % (1 + 0.4); // mouse shifts wind
		const windAmp = H * band.amp * (0.5 + bs * 0.6 + dragEnergy * 0.4);
		tpX.beginPath();
		for (let x = 0; x <= W; x += 4) {
			const nx = x / W;
			const y =
				windY +
				Math.sin((nx + windOffset) * Math.PI * 2 * band.freq + band.phase) *
					windAmp +
				Math.sin(
					(nx + windOffset) * Math.PI * 3.7 * band.freq + t * band.speed * 3
				) *
					windAmp *
					0.3 +
				(rmy - 0.5) * windAmp * 0.4;
			x === 0 ? tpX.moveTo(0, y) : tpX.lineTo(x, y);
		}
		const windAlpha = (0.04 + i * 0.005 + bs * 0.03) * (1 + dragEnergy * 0.5);
		tpX.strokeStyle = `rgba(175,215,235,${windAlpha})`;
		tpX.lineWidth = 0.6 + Math.random() * 0.6;
		tpX.stroke();
	});
	// Extra bold wind gusts when dragging
	if (dragEnergy > 0.3) {
		for (let g = 0; g < 3; g++) {
			const gY = rmy * H * 0.55;
			tpX.beginPath();
			const windOffset2 = t * 0.0003;
			for (let x = 0; x <= W; x += 3) {
				const nx = x / W;
				const y =
					gY +
					(rmx - 0.5) * H * 0.06 +
					Math.sin(nx * Math.PI * 3 + windOffset2 * 8 + g) * H * 0.018 * dragEnergy +
					(nx - rmx) * H * 0.04 * dragEnergy;
				x === 0 ? tpX.moveTo(0, y) : tpX.lineTo(x, y);
			}
			tpX.strokeStyle = `rgba(195,230,245,${dragEnergy * 0.12 - g * 0.03})`;
			tpX.lineWidth = 1.2 - g * 0.3;
			tpX.stroke();
		}
	}

	// ── Full-screen rain ──
	const rainIntensity = isActive ? 0.7 + bs * 0.5 : 0.35;
	// Wind angle from mouse movement
	const windAngle = -0.06 + (rmx - 0.5) * 0.06 + mvx * 0.008;
	RAINDROPS2.forEach((drop, i) => {
		// Time-based update preserves the reference speed at ~60fps while
		// preventing device frame rate from changing how fast the rain falls.
		rainY[i] += drop.speed * (0.6 + rainIntensity) * (dtMs * 0.001);
		const cycle = Math.floor(rainY[i]);
		const rainProgress = fract(rainY[i]);
		const baseX = scatteredRainX(drop, i, cycle, t);
		const rx2 = ((baseX + windAngle * rainProgress * 2.5 + 1.2) % 1.4) - 0.1;
		const ry2 = rainProgress * H;
		const len = drop.len * (0.8 + rainIntensity * 0.4);
		const x1 = rx2 * W,
			y1 = ry2;
		const x2 = x1 + windAngle * len * 8,
			y2 = y1 - len;
		// fade out near lake surface (ripple effect there)
		const nearLake = Math.max(0, 1 - Math.abs(ry2 - lakeTop) / 60);
		const alpha = drop.alpha * rainIntensity * (1 - nearLake * 0.7);
		if (alpha < 0.01) return;
		tpX.beginPath();
		tpX.moveTo(x1, y1);
		tpX.lineTo(x2, y2);
		tpX.strokeStyle = `rgba(155,210,240,${alpha})`;
		tpX.lineWidth = drop.thickness;
		tpX.stroke();
		// Splash on lake surface
		if (
			y1 > lakeTop - 5 &&
			y1 < lakeTop + 12 &&
			Math.random() < 0.003 * rainIntensity
		) {
			LAKE_RIPPLES.push({
				x: x1,
				y: lakeTop + 2,
				r: 0,
				maxR: 8 + Math.random() * 12,
				alpha: 0.22,
				speed: 0.7 + Math.random() * 0.5
			});
		}
	});

	// Mouse wave overlay on lake
	drawMouseWave(t, [105, 195, 215], 0.06);
}

/* ══════════════════════════════════════
   5. CLASSICAL — parchment + pink-red wave in sync with sheet music
══════════════════════════════════════ */
const NOTES_PER_STAVE = 16;
const NOTE_DATA = Array.from({ length: 4 }, (_, si) =>
	Array.from({ length: NOTES_PER_STAVE }, (_, ni) => {
		const r = RNG(si * 100 + ni * 7);
		return {
			pos: Math.floor(r() * 9) - 4,
			type: Math.floor(r() * 3),
			rest: r() > 0.82
		};
	})
);
// Track scroll phase per stave — used to sync the wave to note playback position
let classicalWavePhase = 0;
function drawClassical(t, bs, ev) {
	const bg2 = bgX.createLinearGradient(0, 0, 0, H);
	bg2.addColorStop(0, "#f8f2e4");
	bg2.addColorStop(0.4, "#f3ebd7");
	bg2.addColorStop(0.8, "#eee2cc");
	bg2.addColorStop(1, "#e9d9be");
	bgX.fillStyle = bg2;
	bgX.fillRect(0, 0, W, H);
	const vg = bgX.createRadialGradient(
		W / 2,
		H / 2,
		H * 0.14,
		W / 2,
		H / 2,
		W * 0.74
	);
	vg.addColorStop(0, "transparent");
	vg.addColorStop(0.62, `rgba(138,92,52,${0.04 + bs * 0.08})`);
	vg.addColorStop(1, `rgba(95,50,20,${0.22 + bs * 0.1})`);
	bgX.fillStyle = vg;
	bgX.fillRect(0, 0, W, H);
	// Mouse warm spot
	const mspot = bgX.createRadialGradient(
		W * rmx,
		H * rmy,
		0,
		W * rmx,
		H * rmy,
		W * (0.25 + dragEnergy * 0.06)
	);
	mspot.addColorStop(
		0,
		`rgba(200,140,80,${0.03 + bs * 0.05 + dragEnergy * 0.03})`
	);
	mspot.addColorStop(1, "transparent");
	bgX.fillStyle = mspot;
	bgX.fillRect(0, 0, W, H);
	// Click ripple warm burst on parchment
	clickRipples.forEach((r) => {
		const g2 = bgX.createRadialGradient(
			r.x * W,
			r.y * H,
			0,
			r.x * W,
			r.y * H,
			W * 0.15 * r.strength
		);
		g2.addColorStop(
			0,
			`rgba(180,60,50,${r.strength * 0.04 * Math.exp(-r.t * 1.5)})`
		);
		g2.addColorStop(1, "transparent");
		bgX.fillStyle = g2;
		bgX.fillRect(0, 0, W, H);
	});

	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);
	const p = sceneProfile();
	const staveY = p.mobile ? [H * 0.24, H * 0.72] : [H * 0.2, H * 0.38, H * 0.56, H * 0.74];
	// Track which stave is "active" (playing) — cycles through them
	const stavePeriod = 8; // seconds per stave cycle
	const activeStave = Math.floor(((t * 0.001) / stavePeriod) % staveY.length);
	// Note playback position within active stave (0..1)
	const notePlayPos = (((t * 0.001) / stavePeriod) % 1) * NOTES_PER_STAVE;

	for (let s = 0; s < staveY.length; s++) {
		const baseY = staveY[s],
			scrollX = (t * 0.021 * (1 + s * 0.42)) % W;
		for (let line = 0; line < 5; line++) {
			const ly = baseY + line * 9;
			const lg = bgX.createLinearGradient(0, 0, W, 0);
			lg.addColorStop(0, "transparent");
			lg.addColorStop(0.04, `rgba(68,32,12,${0.13 + bs * 0.06})`);
			lg.addColorStop(0.96, `rgba(68,32,12,${0.13 + bs * 0.06})`);
			lg.addColorStop(1, "transparent");
			bgX.strokeStyle = lg;
			bgX.lineWidth = 0.72;
			bgX.beginPath();
			bgX.moveTo(0, ly);
			bgX.lineTo(W, ly);
			bgX.stroke();
		}
		bgX.font = `${34 + s * 2}px serif`;
		bgX.fillStyle = `rgba(78,32,15,${0.19 + bs * 0.09})`;
		bgX.fillText("𝄞", Math.max(18, 40 - scrollX * 0.03), baseY + 26);
		for (let b2 = 0; b2 < 6; b2++) {
			const bx = (b2 * (W / 5.5) + W - scrollX) % W;
			if (bx > 58 && bx < W - 10) {
				bgX.beginPath();
				bgX.moveTo(bx, baseY - 2);
				bgX.lineTo(bx, baseY + 38);
				bgX.strokeStyle = `rgba(58,22,10,${0.11 + bs * 0.05})`;
				bgX.lineWidth = 1.2;
				bgX.stroke();
			}
		}
		NOTE_DATA[s].forEach((nd, ni) => {
			if (p.mobile && ni % 2 === 1) return;
			const nx2 = (ni * (W / NOTES_PER_STAVE) + W - scrollX * 1.85) % W;
			if (nx2 < 58 || nx2 > W - 10) return;
			const ny2 = baseY + nd.pos * 4.5 + 18;
			// active stave notes near playhead glow pink-red
			const isNearPlayhead = s === activeStave && Math.abs(ni - notePlayPos) < 1.2;
			const na = isNearPlayhead ? 0.22 + bs * 0.18 : 0.13 + bs * 0.13;
			if (nd.rest) {
				bgX.strokeStyle = `rgba(58,22,10,${na * 0.8})`;
				bgX.lineWidth = 1.5;
				bgX.beginPath();
				bgX.moveTo(nx2 - 6, ny2 - 6);
				bgX.lineTo(nx2 + 6, ny2 - 6);
				bgX.stroke();
				return;
			}
			const vib =
				Math.sin(t * 0.003 + ni * 1.2) * bs * 2.5 +
				(rmx - 0.5) * bs * 3 +
				mvx * 0.4 * Math.sin(ni * 0.3);
			// Note color — pink-red tint near playhead
			const noteR = isNearPlayhead
				? `rgba(165,18,40,${na + 0.08})`
				: `rgba(52,20,8,${na})`;
			bgX.beginPath();
			bgX.ellipse(nx2 + vib, ny2, 5.5, 4.2, -0.2, 0, Math.PI * 2);
			bgX.fillStyle = noteR;
			bgX.fill();
			const stemDir = nd.pos < 0 ? 1 : -1;
			bgX.beginPath();
			bgX.moveTo(nx2 + vib + 4.5, ny2);
			bgX.lineTo(nx2 + vib + 4.5, ny2 - 28 * stemDir);
			bgX.strokeStyle = noteR;
			bgX.lineWidth = 1.2;
			bgX.stroke();
			if (nd.type === 2) {
				bgX.beginPath();
				bgX.moveTo(nx2 + vib + 4.5, ny2 - 28 * stemDir);
				bgX.quadraticCurveTo(
					nx2 + vib + 22,
					ny2 - 20 * stemDir,
					nx2 + vib + 18,
					ny2 - 10 * stemDir
				);
				bgX.strokeStyle = `rgba(52,20,8,${na * 0.8})`;
				bgX.lineWidth = 1;
				bgX.stroke();
			}
			if (nd.pos > 4 || nd.pos < -4) {
				bgX.beginPath();
				bgX.moveTo(nx2 - 7, ny2);
				bgX.lineTo(nx2 + 12, ny2);
				bgX.strokeStyle = `rgba(52,20,8,${na * 0.7})`;
				bgX.lineWidth = 0.8;
				bgX.stroke();
			}
		});
	}

	// Ink waves — strongly mouse-reactive
	const mxBC = rmx - 0.5,
		myBC = rmy - 0.5;
	[
		[0.72, 22, 0.62, 0.000038, 0.15, 1.8],
		[0.65, 17, 0.98, 0.00005, 0.12, 1.4],
		[0.79, 15, 0.43, 0.000027, 0.11, 2.0],
		[0.59, 11, 1.52, 0.000063, 0.09, 1.0]
	].forEach(([yc, a, fm, sp, op, w], i) => {
		const mouseYShift = myBC * H * 0.1 * (1 - i * 0.1);
		const yB = H * yc + mouseYShift;
		const mxAmpBoost =
			1 + Math.abs(mxBC) * 2.2 + dragEnergy * 1.5 + Math.abs(myBC) * 0.9;
		const amp = a * (0.48 + 0.52 * bs) * mxAmpBoost;
		const fmMod = fm * (1 + mxBC * 0.5 + mvx * 0.045);
		const ph = t * sp;
		fxX.beginPath();
		for (let x = 0; x <= W; x += 4) {
			const nx = x / W;
			let y =
				yB +
				Math.sin(nx * Math.PI * 2 * fmMod + ph * 5 + mxBC * nx * 4.5) * amp +
				mxBC * amp * 0.55 * Math.sin(nx * Math.PI * 2.5 + i) +
				Math.sin(nx * Math.PI * 3 * fmMod * 0.7 + ph * 3.5 + i) * amp * 0.42 +
				mvx * amp * 0.09 * Math.sin(nx * Math.PI * 2.2) +
				mvy * amp * 0.06 * Math.cos(nx * Math.PI * 1.8 + i);
			for (const r of clickRipples) {
				const dx = nx - r.x,
					age = r.t,
					wf = age * 0.5,
					sp2 = 0.1 + age * 0.15;
				y +=
					r.strength *
					amp *
					0.24 *
					Math.exp(-Math.pow(Math.abs(dx) - wf, 2) / (sp2 * sp2)) *
					Math.exp(-age * 1.0) *
					Math.sin((Math.abs(dx) - wf) * 18);
			}
			x === 0 ? fxX.moveTo(0, y) : fxX.lineTo(x, y);
		}
		const distToMouse = Math.abs(rmy - yc);
		const proximityBoost = Math.exp(-distToMouse * 3.5) * 0.45;
		fxX.strokeStyle = `rgba(68,26,15,${
			(op + proximityBoost) * (0.38 + 0.62 * bs)
		})`;
		fxX.lineWidth = w * (1 + proximityBoost * 1.6);
		fxX.stroke();
	});

	// ── Pink-Red wave — synced to sheet music + strongly mouse-reactive ──
	const scrollSync = notePlayPos / NOTES_PER_STAVE;
	classicalWavePhase += isActive ? 0.0018 : 0.0008;
	const pinkWaveLayers = [
		{
			yFrac: 0.5,
			ampFrac: 0.038,
			freq: 1.6,
			lineW: 3.0,
			alpha: 0.52 + bs * 0.3
		},
		{
			yFrac: 0.5,
			ampFrac: 0.062,
			freq: 1.6,
			lineW: 9.0,
			alpha: 0.13 + bs * 0.18
		},
		{
			yFrac: 0.5,
			ampFrac: 0.028,
			freq: 3.2,
			lineW: 1.5,
			alpha: 0.36 + bs * 0.24
		}
	];
	pinkWaveLayers.forEach(({ yFrac, ampFrac, freq, lineW, alpha }, li) => {
		// Strong vertical mouse tracking — wave follows cursor Y
		const wY = H * (yFrac + (rmy - 0.5) * (p.mobile ? 0.06 : 0.22));
		const wAmp =
			H *
			ampFrac * (p.mobile ? 0.62 : 1) *
			(0.5 + bs * 0.7) *
			(1 +
				Math.abs(rmx - 0.5) * 1.8 +
				dragEnergy * 1.2 +
				Math.abs(rmy - 0.5) * 0.6);
		const freqMod = freq * (1 + (rmx - 0.5) * 0.5 + mvx * 0.04);
		const ph =
			classicalWavePhase * 5.5 + scrollSync * Math.PI * 4 + (rmx - 0.5) * 2.5;
		tpX.beginPath();
		for (let x = 0; x <= W; x += 3) {
			const nx = x / W;
			let y =
				wY +
				Math.sin(nx * Math.PI * 2 * freqMod + ph) * wAmp +
				Math.sin(nx * Math.PI * 3.5 * freqMod + ph * 0.72) * wAmp * 0.4 +
				Math.sin(nx * Math.PI * 1.1 + ph * 0.4) * wAmp * 0.24 +
				(rmx - 0.5) * wAmp * 1.0 * Math.sin(nx * Math.PI * 2 + li) + // strong horizontal bend
				mvx * wAmp * 0.1 * Math.sin(nx * Math.PI * 3.5) + // velocity drag
				mvy * wAmp * 0.06 * Math.cos(nx * Math.PI * 2.2 + li); // vertical velocity
			if (dragEnergy > 0.1)
				y += dragEnergy * wAmp * 0.6 * Math.sin(nx * Math.PI * 5.5 + ph * 1.3);
			for (const r of clickRipples) {
				const dx = nx - r.x,
					age = r.t,
					wf = age * 0.5,
					sp2 = 0.09 + age * 0.13;
				y +=
					r.strength *
					wAmp *
					0.45 *
					Math.exp(-Math.pow(Math.abs(dx) - wf, 2) / (sp2 * sp2)) *
					Math.exp(-age * 0.8) *
					Math.sin((Math.abs(dx) - wf) * 22);
			}
			x === 0 ? tpX.moveTo(0, y) : tpX.lineTo(x, y);
		}
		const grad = tpX.createLinearGradient(0, wY - wAmp * 1.5, 0, wY + wAmp * 1.5);
		grad.addColorStop(0, `rgba(230,100,130,${alpha * 0.4})`);
		grad.addColorStop(0.4, `rgba(200,30,60,${alpha})`);
		grad.addColorStop(0.6, `rgba(180,18,45,${alpha})`);
		grad.addColorStop(1, `rgba(240,130,160,${alpha * 0.3})`);
		tpX.strokeStyle = grad;
		tpX.lineWidth = lineW * (1 + dragEnergy * 0.4);
		tpX.stroke();
	});

	const rph = t * 0.000027;
	for (let r2 = 0; r2 < 3; r2++) {
		const yoff = H * (0.5 + r2 * 0.042);
		fxX.beginPath();
		for (let x = 0; x <= W; x += 3) {
			const nx = x / W;
			const y =
				yoff +
				Math.sin(nx * Math.PI * 2 * 0.6 + rph * (4.5 + r2)) *
					(18 + bs * 13) *
					(0.3 + r2 * 0.2) +
				Math.sin(nx * Math.PI * 1.4 + rph * (3 + r2)) * 8 +
				(rmx - 0.5) * 12 * Math.sin(nx * Math.PI * 1.2) +
				mvx * 5 * Math.sin(nx * Math.PI * 2);
			x === 0 ? fxX.moveTo(0, y) : fxX.lineTo(x, y);
		}
		fxX.strokeStyle = `rgba(152,22,42,${(0.11 + bs * 0.19) * (1 - r2 * 0.25)})`;
		fxX.lineWidth = 1.8 - r2 * 0.3;
		fxX.stroke();
	}
	drawMouseWave(t, [152, 22, 42], 0.05);
}

/* ══════════════════════════════════════
   6. JAZZ
══════════════════════════════════════ */
const JP = {
	bg: "#0d0804",
	cream: "#f0e4c0",
	red: "#c02818",
	orange: "#de7808",
	gold: "#eea818",
	navy: "#0c0e1a",
	teal: "#167585",
	black: "#060402"
};
function jpRect(c, x, y, w, h, col, a) {
	c.save();
	c.globalAlpha = a;
	c.fillStyle = col;
	c.fillRect(x, y, w, h);
	c.restore();
}
function jpCircle(c, cx, cy, r, col, a, fill) {
	c.save();
	c.globalAlpha = a;
	if (fill) {
		c.fillStyle = col;
		c.beginPath();
		c.arc(cx, cy, r, 0, Math.PI * 2);
		c.fill();
	} else {
		c.strokeStyle = col;
		c.lineWidth = r * 0.12;
		c.beginPath();
		c.arc(cx, cy, r, 0, Math.PI * 2);
		c.stroke();
	}
	c.restore();
}
function drawVinyl(ctx, cx, cy, R2, t2, bs2) {
	jpCircle(ctx, cx, cy, R2, JP.cream, 0.88, true);
	ctx.save();
	ctx.translate(cx, cy);
	ctx.rotate(t2 * 0.00028 * (1 + bs2 * 0.5));
	for (let i = 0; i < 7; i++) {
		ctx.globalAlpha = 0.11 + i * 0.03;
		ctx.strokeStyle = JP.black;
		ctx.lineWidth = 1.1;
		ctx.beginPath();
		ctx.arc(0, 0, R2 * (0.38 + i * 0.082), 0, Math.PI * 2);
		ctx.stroke();
	}
	ctx.globalAlpha = 1;
	ctx.fillStyle = JP.black;
	ctx.beginPath();
	ctx.arc(0, 0, R2 * 0.35, 0, Math.PI * 2);
	ctx.fill();
	ctx.fillStyle = JP.orange;
	ctx.globalAlpha = 0.17 + bs2 * 0.1;
	ctx.beginPath();
	ctx.arc(0, 0, R2 * 0.28, 0, Math.PI * 2);
	ctx.fill();
	ctx.globalAlpha = 1;
	ctx.fillStyle = JP.cream;
	ctx.beginPath();
	ctx.arc(0, 0, R2 * 0.045, 0, Math.PI * 2);
	ctx.fill();
	ctx.restore();
}
function drawSax(ctx, cx, cy, scale, t2, bs2) {
	const s = scale;
	ctx.save();
	ctx.translate(cx, cy);
	ctx.save();
	ctx.globalAlpha = 0.82;
	ctx.strokeStyle = JP.gold;
	ctx.lineWidth = s * 0.08;
	ctx.lineCap = "round";
	ctx.beginPath();
	ctx.moveTo(0, -s * 0.5);
	ctx.bezierCurveTo(s * 0.3, -s * 0.4, s * 0.4, -s * 0.1, s * 0.25, s * 0.1);
	ctx.bezierCurveTo(s * 0.1, s * 0.3, -s * 0.1, s * 0.4, -s * 0.15, s * 0.55);
	ctx.stroke();
	for (let k = 0; k < 5; k++) {
		const kp = k / 4,
			kx = s * (0.08 * Math.sin(kp * Math.PI * 1.5) * 0.8),
			ky = s * (-0.4 + kp * 0.85);
		const kpulse = 1 + Math.sin(t2 * 0.004 + k * 0.7) * bs2 * 0.06;
		ctx.globalAlpha = 0.5 + bs2 * 0.2;
		ctx.strokeStyle = JP.cream;
		ctx.lineWidth = s * 0.015;
		ctx.beginPath();
		ctx.arc(kx + s * 0.18, ky, s * 0.028 * kpulse, 0, Math.PI * 2);
		ctx.stroke();
	}
	ctx.restore();
	ctx.save();
	ctx.globalAlpha = 0.7;
	ctx.strokeStyle = JP.gold;
	ctx.lineWidth = s * 0.055;
	ctx.beginPath();
	ctx.ellipse(
		-s * 0.05,
		s * 0.58,
		s * 0.18,
		s * 0.11,
		Math.PI / 4,
		0,
		Math.PI * 2
	);
	ctx.stroke();
	ctx.restore();
	ctx.restore();
}
function drawJazz(t, bs, ev) {
	const p = sceneProfile();
	bgX.fillStyle = JP.bg;
	bgX.fillRect(0, 0, W, H);
	// Color blocks shift slightly with mouse drag
	const blockShift = dragEnergy * W * 0.015;
	jpRect(
		bgX,
		(p.mobile ? W * 0.58 : W * 0.44) - blockShift,
		0,
		(p.mobile ? W * 0.42 : W * 0.56) + blockShift,
		H * 0.6,
		JP.red,
		0.88
	);
	jpRect(bgX, 0, p.mobile ? H * 0.63 : H * 0.55, p.mobile ? W * 0.78 : W * 0.62, p.mobile ? H * 0.37 : H * 0.45, JP.navy, 0.92);
	jpRect(bgX, p.mobile ? W * 0.72 : W * 0.62, p.mobile ? H * 0.76 : H * 0.72, p.mobile ? W * 0.28 : W * 0.38, p.mobile ? H * 0.24 : H * 0.28, JP.teal, p.mobile ? 0.28 : 0.44);
	const sp = bgX.createRadialGradient(
		W * rmx,
		H * 0.04,
		0,
		W * rmx,
		H * 0.07,
		W * (0.58 + dragEnergy * 0.1)
	);
	sp.addColorStop(0, `rgba(238,162,24,${0.09 + bs * 0.16 + dragEnergy * 0.04})`);
	sp.addColorStop(0.42, `rgba(175,88,12,${0.04 + bs * 0.07})`);
	sp.addColorStop(1, "transparent");
	bgX.fillStyle = sp;
	bgX.fillRect(0, 0, W, H);
	clickRipples.forEach((r) => {
		const g2 = bgX.createRadialGradient(
			r.x * W,
			r.y * H,
			0,
			r.x * W,
			r.y * H,
			W * 0.18 * r.strength
		);
		g2.addColorStop(
			0,
			`rgba(238,162,24,${r.strength * 0.08 * Math.exp(-r.t * 1.3)})`
		);
		g2.addColorStop(1, "transparent");
		bgX.fillStyle = g2;
		bgX.fillRect(0, 0, W, H);
	});
	fxX.clearRect(0, 0, W, H);
	tpX.clearRect(0, 0, W, H);
	const vR = Math.min(W, H) * (p.mobile ? 0.34 : 0.26) * (1 + bs * 0.012 + clickImpulse * 0.015);
	// Vinyl wobbles slightly with drag
	const vOx = dragEnergy * W * 0.008,
		vOy = dragEnergy * H * 0.005;
	const vinylX = p.mobile ? W * 0.02 : W * 0.22;
	const vinylY = p.mobile ? H * 0.58 : H * 0.44;
	drawVinyl(bgX, vinylX + vOx, vinylY + vOy, vR, t, bs);
	if (bs > 0.05) {
		const gl = bgX.createRadialGradient(
			vinylX + vOx,
			vinylY + vOy,
			vR * 0.9,
			vinylX + vOx,
			vinylY + vOy,
			vR * 1.52
		);
		gl.addColorStop(0, JP.gold);
		gl.addColorStop(1, "transparent");
		bgX.globalAlpha = bs * 0.18;
		bgX.fillStyle = gl;
		bgX.beginPath();
		bgX.arc(vinylX + vOx, vinylY + vOy, vR * 1.52, 0, Math.PI * 2);
		bgX.fill();
		bgX.globalAlpha = 1;
	}
	drawSax(
		fxX,
		(p.mobile ? W * 0.79 : W * 0.68) + (rmx - 0.5) * W * (p.mobile ? 0.006 : 0.02),
		(p.mobile ? H * 0.29 : H * 0.28) + (rmy - 0.5) * H * (p.mobile ? 0.006 : 0.02),
		Math.min(W, H) * (p.mobile ? 0.20 : 0.27),
		t,
		bs
	);
	const pkY = H * (p.mobile ? 0.86 : 0.78),
		pkH = H * (p.mobile ? 0.14 : 0.2),
		pkW = W * (p.mobile ? 0.94 : 0.72),
		wkCount = p.mobile ? 9 : 14,
		kww = pkW / wkCount;
	const blackPat = [0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 0, 1];
	for (let i = 0; i < wkCount; i++) {
		const kx = i * kww,
			pulse =
				0.92 + Math.sin(t * 0.003 + i * 0.4) * bs * 0.06 + clickImpulse * 0.04;
		tpX.save();
		tpX.globalAlpha = pulse;
		tpX.fillStyle = i % 2 === 0 ? JP.cream : "#e3d7b0";
		tpX.fillRect(kx + 1, pkY, kww - 2, pkH);
		tpX.strokeStyle = "rgba(0,0,0,.2)";
		tpX.lineWidth = 1;
		tpX.strokeRect(kx + 1, pkY, kww - 2, pkH);
		tpX.restore();
	}
	const bkw = kww * 0.6,
		bkh = pkH * 0.6;
	for (let i = 0; i < wkCount - 1; i++) {
		if (!blackPat[i]) continue;
		const kx = (i + 1) * kww - bkw / 2,
			pulse =
				0.84 + Math.sin(t * 0.003 + i * 0.4 + 1) * bs * 0.07 + clickImpulse * 0.03;
		tpX.save();
		tpX.globalAlpha = pulse;
		tpX.fillStyle = JP.black;
		tpX.fillRect(kx, pkY, bkw, bkh);
		tpX.restore();
	}
	tpX.save();
	tpX.globalAlpha = 0.13;
	tpX.fillStyle = JP.cream;
	tpX.beginPath();
	tpX.arc(W, 0, W * 0.22, 0, Math.PI * 0.5);
	tpX.closePath();
	tpX.fill();
	tpX.restore();
	jpCircle(tpX, W * 0.5, H * 0.1, W * 0.09, JP.navy, 0.8, true);
	tpX.save();
	tpX.globalAlpha = 0.52 + bs * 0.14 + dragEnergy * 0.08;
	tpX.strokeStyle = JP.gold;
	tpX.lineWidth = W * 0.016;
	tpX.beginPath();
	tpX.arc(W * 0.74, H * 0.62, W * 0.11, -Math.PI * 0.5, Math.PI * 0.55);
	tpX.stroke();
	tpX.restore();
	jpCircle(tpX, W * 0.86, H * 0.68, W * 0.05, JP.red, 0.62 + bs * 0.14, true);
	for (let i = 0; i < (p.mobile ? 3 : 6); i++) {
		const sx = W * (0.56 + i * 0.042);
		// String wobble strongly reacts to mouse horizontal position and velocity
		const wobble =
			Math.sin(t * 0.002 * (1 + i * 0.1) + i * 0.8) *
				(3 + bs * (p.mobile ? 8 : 17)) *
				(1 + (rmx - 0.5) * (p.mobile ? 0.35 : 1.8) + dragEnergy * (p.mobile ? 0.25 : 0.8)) +
			mvx * 0.5;
		// Each string glows more when cursor is nearby horizontally
		const distX = Math.abs(rmx - (0.56 + i * 0.042));
		const strProximity = Math.exp(-distX * 8) * 0.6;
		const grad = fxX.createLinearGradient(0, 0, 0, H);
		grad.addColorStop(0, "rgba(238,172,24,0)");
		grad.addColorStop(
			0.1,
			`rgba(238,172,24,${0.44 + bs * 0.32 + strProximity * 0.3})`
		);
		grad.addColorStop(
			0.9,
			`rgba(238,172,24,${0.38 + bs * 0.26 + strProximity * 0.25})`
		);
		grad.addColorStop(1, "rgba(238,172,24,0)");
		fxX.beginPath();
		for (let y2 = 0; y2 <= H; y2 += 3) {
			const xo =
				wobble * Math.sin((y2 / H) * Math.PI * 3 + t * 0.0015 + i) +
				(rmx - 0.5) * 8 * Math.sin((y2 / H) * Math.PI * 2 + i) + // horizontal mouse bends strings
				mvy * 3 * Math.cos((y2 / H) * Math.PI + i); // vertical velocity
			let xr = 0;
			for (const r of clickRipples) {
				const dy = y2 / H - r.y,
					age = r.t,
					wf = age * 0.5,
					sp2 = 0.08 + age * 0.12;
				xr +=
					r.strength *
					wobble *
					0.5 *
					Math.exp(-Math.pow(Math.abs(dy) - wf, 2) / (sp2 * sp2)) *
					Math.exp(-age * 1.1) *
					Math.sin((Math.abs(dy) - wf) * 22);
			}
			y2 === 0 ? fxX.moveTo(sx + xo + xr, 0) : fxX.lineTo(sx + xo + xr, y2);
		}
		fxX.strokeStyle = grad;
		fxX.lineWidth = (0.8 + (6 - i) * 0.18) * (1 + strProximity);
		fxX.stroke();
	}
	[
		[0.88, 18, 1.1, 0.000058, JP.gold, 0.18],
		[0.93, 12, 1.6, 0.000072, JP.orange, 0.14]
	].forEach(([yc, amp, fm, sp, col, op]) => {
		const yB = H * yc,
			a2 =
				amp *
				(0.5 + 0.5 * bs) *
				(1 + ev * 0.3) *
				(1 + Math.abs(rmx - 0.5) * 0.6 + dragEnergy * 0.4),
			ph = t * sp;
		tpX.save();
		tpX.globalAlpha = op + bs * 0.07;
		tpX.beginPath();
		for (let x = 0; x <= W; x += 3) {
			const nx = x / W;
			let y =
				yB +
				Math.sin(nx * Math.PI * 2 * fm + ph * 8) * a2 +
				(rmx - 0.5) * a2 * 0.5 * Math.sin(nx * Math.PI * 1.4) +
				mvx * a2 * 0.02;
			for (const r of clickRipples) {
				const dx = nx - r.x,
					age = r.t,
					wf = age * 0.5,
					sp2 = 0.08 + age * 0.12;
				y +=
					r.strength *
					a2 *
					0.25 *
					Math.exp(-Math.pow(Math.abs(dx) - wf, 2) / (sp2 * sp2)) *
					Math.exp(-age * 1.1) *
					Math.sin((Math.abs(dx) - wf) * 20);
			}
			x === 0 ? tpX.moveTo(0, y) : tpX.lineTo(x, y);
		}
		tpX.lineTo(W, H);
		tpX.lineTo(0, H);
		tpX.closePath();
		tpX.fillStyle = col;
		tpX.fill();
		tpX.restore();
	});
	drawMouseWave(t, [238, 162, 24], 0.1);
}

/* ═══ BARS ══ */
const barBox = get("bar-counter");
function addBreathBar() {
	if (barBox.children.length >= 24) return;
	const th = T[mode],
		c = barBox.children.length,
		h = Math.round(2 + (30 - 2) * ((c + 1) / 24));
	const b = document.createElement("div");
	b.className = "breath-bar";
	b.style.height = "0";
	b.style.background = th.barCol;
	barBox.appendChild(b);
	requestAnimationFrame(() => {
		b.style.height = h + "px";
	});
}
function clearBars() {
	while (barBox.firstChild) barBox.removeChild(barBox.firstChild);
}

/* ═══ AUDIO ══ */
let actx = null,
	mGain = null,
	anodes = [];
function killSynth(cb) {
	if (!actx) {
		cb && cb();
		return;
	}
	const closingCtx = actx;
	const closingGain = mGain;
	const closingNodes = anodes.slice();
	try {
		closingGain.gain.cancelScheduledValues(closingCtx.currentTime);
		closingGain.gain.linearRampToValueAtTime(0, closingCtx.currentTime + 0.45);
	} catch (e) {}
	if (actx === closingCtx) {
		actx = null;
		mGain = null;
		anodes = [];
	}
	later(() => {
		closingNodes.forEach((n) => { try { n.stop(); } catch (e) {} });
		try { closingCtx.close(); } catch (e) {}
		cb && cb();
	}, 520);
}
function killMusic() {
	if (musicAudio) {
		try {
			musicAudio.pause();
			musicAudio.src = "";
		} catch (e) {}
		musicAudio = null;
	}
}
function killAudio(cb) {
	clearAudioTimers();
	killMusic();
	killSynth(cb);
}
function mkCtx() {
	const AudioCtx = window.AudioContext || window.webkitAudioContext;
	if (!AudioCtx) return false;
	actx = new AudioCtx();
	mGain = actx.createGain();
	mGain.gain.setValueAtTime(0, actx.currentTime);
	mGain.gain.linearRampToValueAtTime(0.5, actx.currentTime + 3);
	mGain.connect(actx.destination);
	if (actx.state === "suspended" && actx.resume) actx.resume().catch(() => {});
	return true;
}
function pinkBuf(sec) {
	const len = actx.sampleRate * sec,
		buf = actx.createBuffer(2, len, actx.sampleRate);
	for (let ch = 0; ch < 2; ch++) {
		const d = buf.getChannelData(ch);
		let b0 = 0,
			b1 = 0,
			b2 = 0,
			b3 = 0,
			b4 = 0,
			b5 = 0,
			b6 = 0;
		for (let i = 0; i < len; i++) {
			const w = Math.random() * 2 - 1;
			b0 = 0.99886 * b0 + w * 0.0555179;
			b1 = 0.99332 * b1 + w * 0.0750759;
			b2 = 0.969 * b2 + w * 0.153852;
			b3 = 0.8665 * b3 + w * 0.3104856;
			b4 = 0.55 * b4 + w * 0.5329522;
			b5 = -0.7616 * b5 - w * 0.016898;
			d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
			b6 = w * 0.115926;
		}
	}
	return buf;
}
function loopNoise(freq, type, vol) {
	const src = actx.createBufferSource();
	src.buffer = pinkBuf(4);
	src.loop = true;
	const f = actx.createBiquadFilter();
	f.type = type || "lowpass";
	f.frequency.value = freq || 600;
	const g = actx.createGain();
	g.gain.value = vol || 0.4;
	src.connect(f);
	f.connect(g);
	g.connect(mGain);
	src.start();
	anodes.push(src);
}
function pad(freqs, type, vol, lfo) {
	freqs.forEach((f, i) => {
		const o = actx.createOscillator();
		o.type = type || "sine";
		o.frequency.value = f;
		const g = actx.createGain();
		g.gain.value = vol || 0.07;
		if (lfo) {
			const l = actx.createOscillator();
			l.frequency.value = lfo + i * 0.015;
			const lg = actx.createGain();
			lg.gain.value = 0.022;
			l.connect(lg);
			lg.connect(g.gain);
			l.start();
			anodes.push(l);
		}
		o.connect(g);
		g.connect(mGain);
		o.start();
		anodes.push(o);
	});
}
function drops() {
	const go = () => {
		if (!actx || !mGain) return;
		const o = actx.createOscillator(),
			g = actx.createGain();
		o.frequency.value =
			[523, 659, 784, 1047][Math.floor(Math.random() * 4)] *
			(1 + Math.random() * 0.015);
		o.type = "sine";
		const n = actx.currentTime;
		g.gain.setValueAtTime(0, n);
		g.gain.linearRampToValueAtTime(0.045, n + 0.012);
		g.gain.exponentialRampToValueAtTime(0.0001, n + 1.3);
		o.connect(g);
		g.connect(mGain);
		o.start(n);
		o.stop(n + 1.4);
		audioLater(go, 1600 + Math.random() * 3800);
	};
	audioLater(go, 700);
}
function bells() {
	const go = () => {
		if (!actx || !mGain) return;
		const f0 = 220 * (1 + Math.random() * 0.04);
		[1, 2.756, 5.4, 8.93].forEach((p) => {
			const o = actx.createOscillator();
			o.type = "sine";
			o.frequency.value = f0 * p;
			const g = actx.createGain();
			const n = actx.currentTime;
			g.gain.setValueAtTime(0, n);
			g.gain.linearRampToValueAtTime(0.036 / (p * 0.5 + 0.5), n + 0.02);
			g.gain.exponentialRampToValueAtTime(0.0001, n + 5 + Math.random() * 4);
			o.connect(g);
			g.connect(mGain);
			o.start(n);
			o.stop(n + 10);
		});
		audioLater(go, 8000 + Math.random() * 13000);
	};
	audioLater(go, 1500);
}
function crickets() {
	const go = () => {
		if (!actx || !mGain) return;
		const buf = actx.createBuffer(1, actx.sampleRate * 0.07, actx.sampleRate);
		const d = buf.getChannelData(0);
		for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.5;
		const s = actx.createBufferSource();
		s.buffer = buf;
		const bp = actx.createBiquadFilter();
		bp.type = "bandpass";
		bp.frequency.value = 4100 + Math.random() * 700;
		bp.Q.value = 9;
		const g = actx.createGain();
		g.gain.value = 0.055;
		s.connect(bp);
		bp.connect(g);
		g.connect(mGain);
		s.start();
		audioLater(go, 55 + Math.random() * 110);
	};
	audioLater(go, 2200);
}
function owl() {
	const go = () => {
		if (!actx || !mGain) return;
		const dur = 0.45 + Math.random() * 0.35,
			o = actx.createOscillator(),
			g = actx.createGain();
		o.type = "sine";
		o.frequency.setValueAtTime(315, actx.currentTime);
		o.frequency.linearRampToValueAtTime(275, actx.currentTime + dur);
		g.gain.setValueAtTime(0, actx.currentTime);
		g.gain.linearRampToValueAtTime(0.045, actx.currentTime + 0.1);
		g.gain.setValueAtTime(0.045, actx.currentTime + dur - 0.1);
		g.gain.linearRampToValueAtTime(0, actx.currentTime + dur);
		o.connect(g);
		g.connect(mGain);
		o.start();
		o.stop(actx.currentTime + dur + 0.05);
		audioLater(go, 9000 + Math.random() * 17000);
	};
	audioLater(go, 6000);
}

/* Nature: rain + wind + water sounds */
function rainSound() {
	loopNoise(1200, "lowpass", 0.38);
	loopNoise(400, "lowpass", 0.28);
	loopNoise(5500, "bandpass", 0.06);
}
function windSound() {
	// Layered wind: low rumble + mid whoosh + high whistle
	loopNoise(180, "lowpass", 0.18);
	loopNoise(620, "bandpass", 0.12);
	// LFO-modulated wind oscillation
	const o = actx.createOscillator(),
		lfo = actx.createOscillator();
	o.type = "sawtooth";
	o.frequency.value = 55;
	lfo.frequency.value = 0.08;
	const lfoG = actx.createGain();
	lfoG.gain.value = 20;
	const filt = actx.createBiquadFilter();
	filt.type = "lowpass";
	filt.frequency.value = 400;
	const g = actx.createGain();
	g.gain.value = 0.06;
	lfo.connect(lfoG);
	lfoG.connect(filt.frequency);
	o.connect(filt);
	filt.connect(g);
	g.connect(mGain);
	o.start();
	lfo.start();
	anodes.push(o, lfo);
}
function waterDrops() {
	// Gentle water drop sounds for lake
	const go = () => {
		if (!actx || !mGain) return;
		const o = actx.createOscillator(),
			g = actx.createGain();
		o.type = "sine";
		const baseFreq = [523, 587, 659, 784, 880][Math.floor(Math.random() * 5)];
		o.frequency.setValueAtTime(baseFreq * 1.5, actx.currentTime);
		o.frequency.exponentialRampToValueAtTime(
			baseFreq * 0.7,
			actx.currentTime + 0.3
		);
		const n = actx.currentTime;
		g.gain.setValueAtTime(0, n);
		g.gain.linearRampToValueAtTime(0.028, n + 0.008);
		g.gain.exponentialRampToValueAtTime(0.0001, n + 0.4);
		o.connect(g);
		g.connect(mGain);
		o.start(n);
		o.stop(n + 0.5);
		audioLater(go, 800 + Math.random() * 3500);
	};
	audioLater(go, 1200);
}

function whoosh(isIn) {
	if (!actx || !mGain) return;
	const dur = isIn ? 3.6 : 4.0,
		buf = actx.createBuffer(1, actx.sampleRate * dur, actx.sampleRate),
		d = buf.getChannelData(0);
	for (let i = 0; i < d.length; i++) {
		const tv = i / d.length;
		d[i] =
			(Math.random() * 2 - 1) *
			(isIn ? Math.sin(tv * Math.PI) : Math.sin((1 - tv) * Math.PI) * 0.82) *
			0.15;
	}
	const s = actx.createBufferSource();
	s.buffer = buf;
	const bp = actx.createBiquadFilter();
	bp.type = "bandpass";
	bp.frequency.value = isIn ? 2100 : 1650;
	bp.Q.value = 0.5;
	const g = actx.createGain();
	g.gain.value = 0.62;
	s.connect(bp);
	bp.connect(g);
	g.connect(mGain);
	s.start();
}

function synthJazz() {
	const bassNotes = [87.31, 98, 110, 130.81, 146.83, 130.81, 110, 98];
	let beatIdx = 0;
	const tempo = 1.4;
	const bassWalk = () => {
		if (!actx || !mGain) return;
		const o = actx.createOscillator(),
			g = actx.createGain();
		o.type = "triangle";
		o.frequency.value = bassNotes[beatIdx % bassNotes.length];
		const n = actx.currentTime;
		g.gain.setValueAtTime(0, n);
		g.gain.linearRampToValueAtTime(0.18, n + 0.04);
		g.gain.exponentialRampToValueAtTime(0.0001, n + tempo * 0.85);
		o.connect(g);
		g.connect(mGain);
		o.start(n);
		o.stop(n + tempo);
		beatIdx++;
		audioLater(bassWalk, tempo * 1000);
	};
	bassWalk();
	const chordSets = [
		[174.61, 220, 293.66],
		[195.99, 246.94, 329.63],
		[220, 277.18, 369.99],
		[174.61, 220, 293.66]
	];
	let chordIdx = 0;
	const strumChord = () => {
		if (!actx || !mGain) return;
		const chord = chordSets[chordIdx % chordSets.length];
		chord.forEach((freq, i) => {
			const o = actx.createOscillator(),
				g = actx.createGain();
			o.type = "sawtooth";
			o.frequency.value = freq;
			const lfo = actx.createOscillator(),
				lg = actx.createGain();
			lfo.frequency.value = 5.5;
			lg.gain.value = 2;
			lfo.connect(lg);
			lg.connect(o.frequency);
			lfo.start();
			anodes.push(lfo);
			const filt = actx.createBiquadFilter();
			filt.type = "lowpass";
			filt.frequency.value = 1800;
			filt.Q.value = 1.2;
			const n = actx.currentTime + i * 0.012;
			g.gain.setValueAtTime(0, n);
			g.gain.linearRampToValueAtTime(0.055, n + 0.08);
			g.gain.exponentialRampToValueAtTime(0.0001, n + tempo * 3.8);
			o.connect(filt);
			filt.connect(g);
			g.connect(mGain);
			o.start(n);
			o.stop(n + tempo * 4);
			anodes.push(o);
		});
		chordIdx++;
		audioLater(strumChord, tempo * 4 * 1000);
	};
	audioLater(strumChord, 200);
	const hat = () => {
		if (!actx || !mGain) return;
		const buf = actx.createBuffer(1, actx.sampleRate * 0.04, actx.sampleRate);
		const d = buf.getChannelData(0);
		for (let i = 0; i < d.length; i++)
			d[i] = (Math.random() * 2 - 1) * Math.exp((-i / d.length) * 30);
		const s = actx.createBufferSource();
		s.buffer = buf;
		const hp = actx.createBiquadFilter();
		hp.type = "highpass";
		hp.frequency.value = 8000;
		const g = actx.createGain();
		g.gain.value = 0.08;
		s.connect(hp);
		hp.connect(g);
		g.connect(mGain);
		s.start();
	};
	const swingTick = () => {
		if (!actx || !mGain) return;
		hat();
		audioLater(() => {
			if (actx && mGain) hat();
		}, tempo * 500 * 0.67);
		audioLater(swingTick, tempo * 1000);
	};
	audioLater(swingTick, 100);
	const riffNotes = [349.23, 392, 440, 493.88, 523.25, 440, 392, 349.23];
	const playRiff = () => {
		if (!actx || !mGain) return;
		riffNotes.forEach((freq, i) => {
			const o = actx.createOscillator(),
				g = actx.createGain();
			o.type = "sawtooth";
			o.frequency.value = freq;
			const filt = actx.createBiquadFilter();
			filt.type = "bandpass";
			filt.frequency.value = freq * 1.5;
			filt.Q.value = 2;
			const n = actx.currentTime + i * tempo * 0.5;
			g.gain.setValueAtTime(0, n);
			g.gain.linearRampToValueAtTime(0.07, n + 0.05);
			g.gain.exponentialRampToValueAtTime(0.0001, n + tempo * 0.45);
			o.connect(filt);
			filt.connect(g);
			g.connect(mGain);
			o.start(n);
			o.stop(n + tempo * 0.5);
			anodes.push(o);
		});
		audioLater(playRiff, 14000 + Math.random() * 8000);
	};
	audioLater(playRiff, 2000);
}

function playMusicTrack(tracks, synthFallback) {
	const tn = get("track-name");
	if (!externalMusic) {
		tn.textContent = "Live Synthesis";
		tn.style.color = T[mode].cueCol;
		tn.classList.add("show");
		if (synthFallback) synthFallback();
		return;
	}
	const shuffled = [...tracks].sort(() => Math.random() - 0.5);
	let tried = 0;
	const tryNext = () => {
		if (!isActive || destroyed) return;
		if (tried >= shuffled.length) {
			tn.textContent = "Live Synthesis";
			tn.style.color = T[mode].cueCol;
			tn.classList.add("show");
			if (synthFallback) synthFallback();
			return;
		}
		const track = shuffled[tried++];
		const audio = new Audio();
		musicAudio = audio;
		audio.crossOrigin = "anonymous";
		audio.preload = "auto";
		audio.src = track.url;
		audio.volume = 0;
		const timeout = audioLater(() => {
			try { audio.pause(); } catch (e) {}
			if (musicAudio === audio) musicAudio = null;
			if (isActive) tryNext();
		}, 8000);
		audio.addEventListener(
			"playing",
			() => {
				clearAudioLater(timeout);
				tn.textContent = track.name;
				tn.style.color = T[mode].cueCol;
				tn.classList.add("show");
				audio.loop = true;
				let vol = 0;
				const fade = audioRepeat(() => {
					if (musicAudio !== audio || !isActive) return clearAudioRepeat(fade);
					vol = Math.min(vol + 0.015, 0.46);
					audio.volume = vol;
					if (vol >= 0.46) clearAudioRepeat(fade);
				}, 120);
			},
			{ once: true }
		);
		audio.addEventListener(
			"error",
			() => {
				clearAudioLater(timeout);
				if (musicAudio === audio) musicAudio = null;
				if (isActive) tryNext();
			},
			{ once: true }
		);
		audio.play().catch(() => {
			clearAudioLater(timeout);
			if (musicAudio === audio) musicAudio = null;
			if (isActive) tryNext();
		});
	};
	tryNext();
}

function startAudio() {
	clearAudioTimers();
	if (!audioEnabled) return;
	if (!mkCtx()) return;
	const tn = get("track-name");
	if (mode === "classical") {
		loopNoise(220, "lowpass", 0.1);
		pad([110, 146.83, 220], "sine", 0.036, 0.04);
		playMusicTrack(CLASSICAL_TRACKS, () => {
			pad([130.81, 164.81, 196, 261.63], "sine", 0.045, 0.05);
			bells();
		});
	} else if (mode === "jazz") {
		loopNoise(300, "lowpass", 0.07);
		playMusicTrack(JAZZ_TRACKS, synthJazz);
	} else if (mode === "nature") {
		tn.classList.remove("show");
		rainSound();
		windSound();
		waterDrops();
		pad([82.41, 110, 130.81], "sine", 0.022, 0.04);
	} else if (mode === "breathe") {
		tn.classList.remove("show");
		const pool = [
			() => {
				loopNoise(650, "lowpass", 0.4);
				loopNoise(180, "highpass", 0.09);
				pad([65.41, 98, 130.81], "sine", 0.07, 0.08);
				drops();
			},
			() => {
				loopNoise(480, "lowpass", 0.44);
				pad([55, 82.41, 110], "sine", 0.074, 0.06);
				bells();
			}
		];
		pool[Math.floor(Math.random() * pool.length)]();
	} else if (mode === "meditate") {
		tn.classList.remove("show");
		const pool = [
			() => {
				loopNoise(380, "lowpass", 0.25);
				pad([110, 146.83, 164.81, 220], "sine", 0.056, 0.05);
				bells();
			},
			() => {
				loopNoise(300, "lowpass", 0.21);
				pad([82.41, 110, 164.81, 246.94], "sine", 0.062, 0.04);
				drops();
			}
		];
		pool[Math.floor(Math.random() * pool.length)]();
	} else if (mode === "anxiety") {
		tn.classList.remove("show");
		const pool = [
			() => {
				loopNoise(580, "lowpass", 0.29);
				loopNoise(140, "highpass", 0.08);
				pad([55, 73.42, 87.31], "sine", 0.06, 0.07);
				crickets();
				owl();
			},
			() => {
				loopNoise(750, "lowpass", 0.4);
				loopNoise(3500, "bandpass", 0.036);
				pad([65.41, 87.31, 130.81], "sine", 0.055, 0.06);
				crickets();
				drops();
			}
		];
		pool[Math.floor(Math.random() * pool.length)]();
	}
}

/* ═══ AFFIRMATIONS ══ */
const affEl = get("affirmation"),
	affTxt = get("affirmation-text");
let affTo = null,
	affRevealTo = null,
	affIdx = 0;
function nextAff() {
	affEl.style.opacity = "0";
	affRevealTo = later(() => {
		affTxt.textContent = AFFIRMATIONS[affIdx % AFFIRMATIONS.length];
		affIdx++;
		affEl.style.opacity = "1";
	}, 1400);
	affTo = later(nextAff, 13000);
}
function startAff() {
	affIdx = Math.floor(Math.random() * AFFIRMATIONS.length);
	affTxt.style.color = T[mode].cueCol;
	affEl.style.transition = "opacity 2.2s ease";
	nextAff();
}
function stopAff() {
	clearLater(affTo);
	clearLater(affRevealTo);
	affTo = null;
	affRevealTo = null;
	affEl.style.opacity = "0";
}

/* ═══ THEME ══ */
function applyTheme(m) {
	const th = T[m];
	root.style.background = th.bg;
	root.style.setProperty("--wv-accent", th.accentHex);
	root.style.setProperty("--wv-accent-rgb", th.accent.join(", "));
	root.style.setProperty("--wv-main", th.textMain);
	root.style.setProperty("--wv-dim", th.pillDim);
	root.style.setProperty("--wv-nav-bg", th.navBg);
	root.style.setProperty("--wv-nav-border", th.navBorder);
	get("vig").style.background = `radial-gradient(ellipse at 50% 50%, transparent 14%, ${th.vigA} 56%, ${th.vigB} 100%)`;
	dotEl.style.background = th.dotCol;
	ringEl.style.border = `1px solid ${th.ringCol}`;
	get("bar-lbl").style.color = th.barLbl;
	get("sl-pattern").style.color = th.slPat;
	get("sl-detail").style.color = th.slDet;
	root.querySelectorAll("[data-mode-choice]").forEach((p) => {
		const isAct = p.dataset.mode === m;
		p.setAttribute("aria-pressed", isAct ? "true" : "false");
	});
	const current = get("mode-current");
	if (current) current.textContent = th.display || m.charAt(0).toUpperCase() + m.slice(1);
	const [rr, rg, rb] = th.accent;
	get("btn-label").style.color = `rgba(${Math.min(rr + 40,255)},${Math.min(rg + 40,255)},${Math.min(rb + 40,255)},.9)`;
	const fl = get("mode-flash");
	fl.style.background = th.flashCol;
	fl.classList.add("pop");
	later(() => fl.classList.remove("pop"), 180);
}

/* ═══ NAV ══ */
const modeTrigger = get("mode-trigger"),
	modeLayer = get("mode-layer"),
	modeSheet = get("mode-sheet");
let modeMenuOpen = false;

function closeModeMenu(restoreFocus = true) {
	if (!modeLayer || !modeTrigger || !modeMenuOpen) return;
	modeMenuOpen = false;
	modeLayer.classList.remove("is-open");
	modeLayer.setAttribute("aria-hidden", "true");
	modeTrigger.setAttribute("aria-expanded", "false");
	if (restoreFocus) modeTrigger.focus({ preventScroll: true });
}
function openModeMenu() {
	if (!modeLayer || !modeTrigger || modeMenuOpen) return;
	modeMenuOpen = true;
	modeLayer.classList.add("is-open");
	modeLayer.setAttribute("aria-hidden", "false");
	modeTrigger.setAttribute("aria-expanded", "true");
	const active = modeLayer.querySelector(`[data-mode-choice][data-mode="${mode}"]`);
	later(() => active?.focus({ preventScroll: true }), 30);
}
function keepDesktopChoiceVisible(choice) {
	const switcher = get("mode-switcher");
	if (!switcher || sceneProfile().mobile) return;
	const sr = switcher.getBoundingClientRect();
	const cr = choice.getBoundingClientRect();
	if (cr.left < sr.left || cr.right > sr.right) {
		switcher.scrollTo({ left: choice.offsetLeft - switcher.clientWidth / 2 + choice.clientWidth / 2, behavior: reduceMotion ? "auto" : "smooth" });
	}
}
function selectMode(nextMode, source) {
	if (!T[nextMode] || nextMode === mode) {
		if (source?.closest("[data-wv-id=\"mode-layer\"]")) closeModeMenu(false);
		return;
	}
	if (isActive) stopSession();
	mode = nextMode;
	applyTheme(mode);
	textAnim = modeTextAnim(mode);
	const th = T[mode];
	buildHeroText(th.heroIdle, th.tagIdle);
	buildCueText(th.cueStart);
	get("sl-pattern").textContent = th.pattern;
	get("sl-detail").textContent = th.detail;
	clearBars();
	get("track-name").classList.remove("show");
	natureCycleT = 0;
	classicalWavePhase = 0;
	LAKE_RIPPLES.length = 0;
	if (source) keepDesktopChoiceVisible(source);
	if (source?.closest("[data-wv-id=\"mode-layer\"]")) closeModeMenu(false);
}

root.querySelectorAll("[data-mode-choice]").forEach((p) => {
	p.addEventListener("click", () => selectMode(p.dataset.mode, p));
	p.addEventListener("mouseenter", () => {
		if (sceneProfile().mobile) return;
		ringEl.style.width = "44px";
		ringEl.style.height = "44px";
	});
	p.addEventListener("mouseleave", () => {
		ringEl.style.width = "26px";
		ringEl.style.height = "26px";
	});
});
modeTrigger?.addEventListener("click", () => modeMenuOpen ? closeModeMenu(false) : openModeMenu());
root.querySelectorAll("[data-mode-close]").forEach((el) => el.addEventListener("click", () => closeModeMenu()));
root.addEventListener("keydown", (e) => {
	if (e.key === "Escape" && modeMenuOpen) {
		e.preventDefault();
		closeModeMenu();
	}
	if (e.key === "Tab" && modeMenuOpen && modeSheet) {
		const focusable = [...modeSheet.querySelectorAll("button:not([disabled])")];
		if (!focusable.length) return;
		const first = focusable[0], last = focusable[focusable.length - 1];
		if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
		else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
	}
});

/* ═══ SESSION ══ */
const fillEl = get("btn-fill"),
	labelEl = get("btn-label"),
	actionNoteEl = get("action-note");
function eio(t) {
	return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
const cueN = {
	inhale: "breathe in slowly",
	hold: "let it rest",
	exhale: "breathe out slowly"
};

function startSession() {
	isActive = true;
	root.classList.add("active");
	textAnim = modeTextAnim(mode);
	const th = T[mode];
	phase = 0;
	timer = 0;
	breathCount = 0;
	clearBars();
	buildHeroText(th.heroAct, th.tagAct);
	buildCueText("breathe in slowly");
	labelEl.textContent = "Finish";
	if (actionNoteEl) actionNoteEl.textContent = "Nothing to complete. Stay for one breath or several.";
	get("breath-btn").setAttribute("aria-pressed", "true");
	get("breath-btn").setAttribute("aria-label", "Finish WatchVA Calm session");
	get("sl-pattern").textContent = th.pattern;
	get("sl-detail").textContent = th.detail;
	get("ir1").style.animation = "none";
	get("ir2").style.animation = "none";
	startAudio();
	if (mode !== "classical" && mode !== "jazz" && mode !== "nature") whoosh(true);
	if (mode === "anxiety") startAff();
	natureCycleT = 0;
}
function stopSession() {
	isActive = false;
	root.classList.remove("active");
	const th = T[mode];
	phase = 0;
	timer = 0;
	bs = 0;
	exhaleVal = 0;
	buildHeroText(th.heroIdle, th.tagIdle);
	buildCueText(th.cueStart);
	labelEl.textContent = "Begin";
	if (actionNoteEl) actionNoteEl.textContent = "One breath is enough. Stay longer if you want.";
	get("breath-btn").setAttribute("aria-pressed", "false");
	get("breath-btn").setAttribute("aria-label", "Begin WatchVA Calm");
	fillEl.style.transform = "scale(1)";
	fillEl.style.boxShadow = "";
	get("ir1").style.animation = "";
	get("ir2").style.animation = "";
	killAudio();
	stopAff();
	get("track-name").classList.remove("show");
}
const btn = get("breath-btn");
btn.addEventListener("click", () => {
	isActive ? stopSession() : startSession();
});
btn.addEventListener("mouseenter", () => {
	ringEl.style.width = "84px";
	ringEl.style.height = "84px";
});
btn.addEventListener("mouseleave", () => {
	ringEl.style.width = "26px";
	ringEl.style.height = "26px";
});

/* ═══ RENDER ══ */
let last = 0;
let renderRaf = 0;
function render(ts) {
	if (destroyed) return;
	if (document.hidden) {
		last = ts;
		renderRaf = requestAnimationFrame(render);
		return;
	}
	const dt = Math.min(ts - last, 50);
	last = ts;
	bgX.clearRect(0, 0, W, H);
	const th = T[mode],
		[ar, ag, ab] = th.accent;

	if (isActive) {
		timer += dt * 0.001;
		const phs = th.phases,
			ph = phs[phase];
		const prog = Math.min(timer / ph.d, 1);
		if (ph.n === "inhale") bs = eio(prog);
		else if (ph.n === "hold") bs = 1;
		else if (ph.n === "exhale") bs = 1 - eio(prog);
		else bs = 0;
		if (timer >= ph.d) {
			timer = 0;
			phase = (phase + 1) % phs.length;
			if (phase === 0) {
				breathCount++;
				addBreathBar();
			}
			buildCueText(cueN[phs[phase].n] || "hold gently");
			if (mode !== "classical" && mode !== "jazz" && mode !== "nature") {
				if (phs[phase].n === "inhale") whoosh(true);
				if (phs[phase].n === "exhale") whoosh(false);
			}
		}
	} else {
		bs = reduceMotion ? 0.15 : (0.5 + 0.5 * Math.sin(ts * 0.00068)) * 0.3;
	}

	const sceneTs = reduceMotion && !isActive ? 0 : ts;
	const ph = th.phases[phase];
	exhaleVal +=
		((!isActive
			? 0
			: ph && ph.n === "exhale"
			? Math.min(timer / ph.d, 1) * 0.85
			: 0) -
			exhaleVal) *
		(1 - Math.exp(-dt * 0.003));

	if (mode === "breathe") drawBreathe(sceneTs, bs, exhaleVal);
	else if (mode === "meditate") drawMeditate(sceneTs, bs, exhaleVal);
	else if (mode === "anxiety") drawAnxiety(sceneTs, bs, exhaleVal);
	else if (mode === "nature") drawNature(sceneTs, bs, exhaleVal, dt);
	else if (mode === "classical") drawClassical(sceneTs, bs, exhaleVal);
	else drawJazz(sceneTs, bs, exhaleVal);

	const sc = 0.68 + bs * 0.78,
		bri = Math.round(bs * 90),
		alpha = 0.04 + bs * 0.24;
	// Click impulse pulses the button
	const btnPulse = 1 + clickImpulse * 0.04;
	fillEl.style.transform = `scale(${(sc * btnPulse).toFixed(3)})`;
	fillEl.style.background = `radial-gradient(circle at 40% 36%, rgba(${ar},${Math.min(
		(ag + bri * 0.6) | 0,
		255
	)},${Math.min((ab + bri * 0.3) | 0, 255)},${alpha + 0.14}) 0%, rgba(${
		(ar * 0.88) | 0
	},${Math.min((ag + bri * 0.35) | 0, 255)},${Math.min(
		(ab + bri * 0.18) | 0,
		255
	)},${alpha + 0.07}) 50%, rgba(${(ar * 0.62) | 0},${(ag * 0.58) | 0},${
		(ab * 0.48) | 0
	},${alpha * 0.5}) 100%)`;
	fillEl.style.borderColor = `rgba(${ar},${ag},${ab},${
		0.18 + bs * 0.58 + clickImpulse * 0.2
	})`;
	fillEl.style.boxShadow = `0 0 ${
		14 + bs * 62 + clickImpulse * 20
	}px rgba(${ar},${ag},${ab},${0.07 + bs * 0.25 + clickImpulse * 0.1}),0 0 ${
		30 + bs * 100
	}px rgba(${ar},${ag},${ab},${0.02 + bs * 0.09})`;

	animateText(reduceMotion && !isActive ? 0 : ts);
	renderRaf = requestAnimationFrame(render);
}
renderRaf = requestAnimationFrame(render);

/* ═══ INIT ══ */
(function initUI() {
	if (!T[mode]) mode = "breathe";
	applyTheme(mode);
	const th = T[mode];
	buildHeroText(th.heroIdle, th.tagIdle);
	buildCueText(th.cueStart);
	get("sl-pattern").textContent = th.pattern;
	get("sl-detail").textContent = th.detail;
	later(() => {
	}, 60);
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => {
			if (destroyed) return;
		});
	}
	later(() => get("hero").classList.add("in"), 140);
	later(() => {
		get("btn-wrap").classList.add("in");
		get("cue").classList.add("show");
		get("session-label").classList.add("show");
		get("bar-counter").classList.add("show");
		get("bar-lbl").classList.add("show");
	}, 560);
})();


    root.__wvCalmDestroy = () => {
      if (destroyed) return;
      destroyed = true;
      try { resizeObserver.disconnect(); } catch (e) {}
      if (cursorRaf) cancelAnimationFrame(cursorRaf);
      if (renderRaf) cancelAnimationFrame(renderRaf);
      timeoutIds.forEach((id) => window.clearTimeout(id));
      intervalIds.forEach((id) => window.clearInterval(id));
      timeoutIds.clear();
      intervalIds.clear();
      clearAudioTimers();
      try { killMusic(); } catch (e) {}
      try {
        anodes.forEach((n) => { try { n.stop(); } catch (e) {} });
        anodes = [];
      } catch (e) {}
      if (actx) {
        const closing = actx;
        actx = null;
        mGain = null;
        try { closing.close(); } catch (e) {}
      }
      root.__wvCalmInitialized = false;
    };
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(SELECTOR)) createWatchvaCalm(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(createWatchvaCalm);
  }

  if (window.__watchvaCalmAssetLoaded) {
    window.__watchvaCalmInitAll?.(document);
    return;
  }

  window.__watchvaCalmAssetLoaded = true;
  window.__watchvaCalmInitAll = initAll;
  initAll(document);

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(SELECTOR)) event.target.__wvCalmDestroy?.();
    event.target.querySelectorAll?.(SELECTOR).forEach((root) => root.__wvCalmDestroy?.());
  });
})();
